import * as i1$1 from '@angular/common/http';
import { HttpErrorResponse, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule, Component, ViewChild, Input, HostBinding } from '@angular/core';
import * as i2$3 from '@angular/platform-browser';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import * as i1$2 from '@ngbracket/ngx-layout/flex';
import * as i3 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { BehaviorSubject, throwError } from 'rxjs';
import * as i1 from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import * as i3$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i5 from '@angular/material/toolbar';
import { MatToolbarModule } from '@angular/material/toolbar';
import * as i3$2 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i7 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import { map, delay, catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import * as i2 from 'ngx-countdown';
import { CountdownModule } from 'ngx-countdown';
import * as i2$1 from '@ngbracket/ngx-layout';
import { FlexLayoutModule, FlexModule } from '@ngbracket/ngx-layout';
import * as i4 from '@angular/material/sidenav';
import { MatSidenavModule } from '@angular/material/sidenav';
import * as i5$2 from '@angular/material/list';
import { MatListModule } from '@angular/material/list';
import * as i5$1 from '@angular/material/divider';
import { MatDividerModule } from '@angular/material/divider';
import * as i8$1 from '@angular/material/progress-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { trigger, state, style, transition, animate, query, group, sequence } from '@angular/animations';
import * as i8 from '@ngbracket/ngx-layout/extended';
import * as i2$2 from '@angular/material/card';
import { MatCardModule } from '@angular/material/card';

/**
 * @description
 *
 * Interface utilizada para montagem dos itens de menu da aplicação. \
 * Os atributos displayName, iconName e route determinam um item único. \
 * Itens com mesmo displayName, iconName e route são considerados iguais e agrupados (as roles são agrupadas). \
 * Um item que possui filhos tem sua route ignorada, pois não é clicável.
 *
 * Se roles não existir no primeiro nível (N), o menu não é exibido. \
 * Se roles não existir no segundo nível (N+1) em diante, o menu assume todas as roles do nível anterior (N).
 *
 * As roles do nível N+1 devem ser um subconjunto de roles do nível N, podendo ser total ou parcial. \
 * Roles do nível N+1 devem respeitar a hierarquia de roles do nível N. \
 * Roles do nível N+1 que não existam no nível N, são removidas e ignoradas.
 *
 * A role: '**' é considerada menu público e sempre será exibido (mesmo sem usuário logado).
 * - Todos os sub menus de um menu público são também públicos, roles são ignoradas.
 *
 * A role: '!!' é considerada menu irrestrito para todos usuários (precisando apenas estar logado).
 * - Todos os sub menus de um menu irrestrito são também irrestritos, roles são ignoradas.
 *
 * @displayName : Texto que irá aparecer.
 * @iconName : Nome do ícone (GoogleFonts Material Icons).
 * @route : Rota.
 * @roles : Roles permitidas para ser exibido.
 * @disabled : Indica se item será desabilitado (exibido com aspecto desabilitado, não clicável).
 * @hidden : Indica se item será escondido (não irá aparecer no menu).
 * @addTopDivider : Indica se deve adicionar um divisor no topo do item.
 * @addBottomDivider : Indica se deve adicionar um divisor no final do item.
 * @order : Ordem de exibição no mesmo nível hierárquico.
 * @smallSize : Indica se o item será exibido em tamanho pequeno (icone menor, vertical menor).
 * @children : Sub itens.
 *
 * Material Icons: https://fonts.google.com/icons?icon.set=Material+Icons
 */
class UnespCoreMenuItem {
    constructor() {
        this.displayName = '';
        this.roles = [];
        this.iconName = '';
        this.route = '';
        this.disabled = false;
        this.hidden = false;
        this.addTopDivider = false;
        this.addBottomDivider = false;
        this.order = 9999;
        this.smallSize = false;
    }
}
/**
 * Wildcard usado para role publica, acesso sem necessidade de login.
 */
const ROLE_WILDCARD_PUBLIC = '**';
/**
 * Wildcard usado para role irrestrita (apenas usuários), acesso com necessidade de login.
 */
const ROLE_WILDCARD_ONLYUSERS = '!!';

/**
 * @description
 *
 * Enum contendo os tipos de mensagens utilizados pelo snackbar juntamente com seu CSS apropriado
 */
var UnespCoreMessageType;
(function (UnespCoreMessageType) {
    UnespCoreMessageType["ERROR"] = "error-snackbar";
    UnespCoreMessageType["WARN"] = "warning-snackbar";
    UnespCoreMessageType["INFO"] = "info-snackbar";
    UnespCoreMessageType["SUCCESS"] = "success-snackbar";
})(UnespCoreMessageType || (UnespCoreMessageType = {}));

/**
 * @description
 *
 * Serviço responsável por manter e disponibilizar o usuário para uso na aplicação
 *
 */
class UnespCoreUserService {
    constructor(environment) {
        this.environment = environment;
        this.user = null;
        this._user$ = new BehaviorSubject(null);
        this.userKey = this.getLocalStoragePrefix() + '_user';
    }
    /**
     * Obtém o usuário da memória ou local storage
     */
    getUser() {
        if (!this.user) {
            this.user = this.getUserLocalStorage();
            this._user$.next(this.user);
        }
        return this.user;
    }
    getLocalStoragePrefix() {
        if (!this.environment.localStoragePrefix) {
            console.error('Atenção desenvolvedor: Definir variável de ambiente localStoragePrefix (Angular Unesp)');
        }
        return this.environment.localStoragePrefix ?? 'app';
    }
    get user$() {
        return this._user$;
    }
    setUser(usuario) {
        this.user = usuario;
        this._user$.next(this.user);
        this.setUserLocalStorage(this.user);
    }
    /**
     * Obtém o usuário diretamente do local storage.
     * Recomendamos utilizar o getUser() para obter usuário diretamente da memória quando possível
     */
    getUserLocalStorage() {
        const usuario = localStorage.getItem(this.userKey);
        if (usuario) {
            this.user = JSON.parse(usuario);
            return this.user;
        }
        return null;
    }
    /**
     * Armazena o usuário no local storage do browser
     */
    setUserLocalStorage(usuario) {
        localStorage.setItem(this.userKey, JSON.stringify(usuario));
    }
    /**
     * Remove o usuário do local storage do browser
     */
    removeUser() {
        this.user = null;
        this._user$.next(null);
        localStorage.removeItem(this.userKey);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreUserService, deps: [{ token: 'environment' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreUserService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreUserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }] });

class UnespCoreUserModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreUserModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreUserModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreUserModule, providers: [UnespCoreUserService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreUserModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [UnespCoreUserService]
                }]
        }] });

/**
 * @description
 *
 * Serviço responsável pelo controle do menu lateral da aplicação, providenciando os métodos
 * de carregamento das opções de menu, abertura, fechamento e refresh.
 *
 */
class UnespCoreMenuNavigationService {
    constructor(router, unespCoreUserService, environment, envMenu) {
        this.router = router;
        this.unespCoreUserService = unespCoreUserService;
        this.environment = environment;
        this.envMenu = envMenu;
        this.appDrawerEnabled = false;
        this.pinnedMenu = false;
        this.publicMenu = false;
        this.currentUrl = new BehaviorSubject('');
        this.processedMenu = [];
        this.itensMenuSource = new BehaviorSubject(this.processedMenu);
        this.currentItensMenu = this.itensMenuSource.asObservable();
        this.screenWidthSubject = new BehaviorSubject(window.innerWidth);
        this.initializePinnedMneu();
        this.registerWindowResideEventListener();
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.currentUrl.next(event.urlAfterRedirects);
            }
        });
        this.refreshMenu();
    }
    /**
     * Inicializa o pinnedMenu do localStorage
     */
    initializePinnedMneu() {
        this.pinnedMenu = localStorage.getItem('unesp-core-pinnedMenu') === 'true';
    }
    /**
     * Registra o evento de resize da tela
     */
    registerWindowResideEventListener() {
        window.addEventListener('resize', this.onScreenResize.bind(this));
    }
    /**
     * Remove o pinnedMenu se a tela for menor que 1280px, usando um debounce de 200ms
     */
    onScreenResize() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = setTimeout(() => {
            const width = window.innerWidth;
            this.screenWidthSubject.next(width);
            if (width < 1280) {
                this.pinnedMenu = false;
            }
        }, 200);
    }
    /**
     * Fecha o menu lateral da aplicação
     */
    closeNav() {
        this.appDrawer?.close();
    }
    /**
     * Abre o menu lateral da aplicação
     */
    openNav() {
        this.appDrawer?.open();
    }
    /**
     * Abre/fecha o menu lateral da aplicação
     */
    toggle() {
        if (!this.pinnedMenu) {
            this.appDrawer?.toggle();
        }
    }
    /**
     * Abre/fecha o menu lateral da aplicação
     */
    togglePinMenu() {
        this.pinnedMenu = !this.pinnedMenu;
        localStorage.setItem('unesp-core-pinnedMenu', this.pinnedMenu ? 'true' : 'false');
    }
    /**
     * Atualiza itens do menu baseado nas roles do usuário
     */
    refreshMenu() {
        this.itensMenuSource.next(this.loadMenu());
    }
    /**
     * Carrega itens do menu baseado nas roles do usuário
     * @returns
     */
    loadMenu() {
        this.processedMenu = [];
        // Usuario logado
        const usuario = this.unespCoreUserService.getUser();
        // Remove menu raiz que não possuem roles
        this.envMenu = this.envMenu.filter((item) => item.roles.length > 0);
        if (this.publicMenu) {
            // Adiciona todas entradas públicas
            this.envMenu.forEach((item) => {
                if (item.roles.includes(ROLE_WILDCARD_PUBLIC)) {
                    item.roles = [ROLE_WILDCARD_PUBLIC];
                    this.normalizeRolesPublicItem(item);
                    this.processedMenu.push(item);
                }
            });
        }
        if (usuario) {
            // Roles do usuário
            const userRoles = usuario.roles;
            // Habilita o menu para usuários
            this.appDrawerEnabled = true;
            this.envMenu.forEach((item) => {
                // Adiciona todas entradas livres
                if (item.roles.includes(ROLE_WILDCARD_ONLYUSERS)) {
                    item.roles = [ROLE_WILDCARD_ONLYUSERS];
                    this.normalizeRolesUnrestrictedItem(item);
                    this.processedMenu.push(item);
                }
                else {
                    if (!item.roles.includes(ROLE_WILDCARD_PUBLIC) &&
                        !item.roles.includes(ROLE_WILDCARD_ONLYUSERS)) {
                        // Adiciona itens (apenas raizes) que possuem roles
                        if (item.roles.some((role) => userRoles.includes(role))) {
                            this.processedMenu.push(item);
                            // Normaliza roles para os filhos
                            this.normalizeRolesRestrictedItem(item, item.roles, true);
                        }
                    }
                }
            });
            // Varre o menu inteiro para:
            // - agrupar itens que são considerados duplicados
            // - remover itens que não possuem roles do usuário
            this.groupAndDeleteMenuItems(this.processedMenu, userRoles);
        }
        else {
            // Habilita o menu para o público (sem usuário logado)
            this.appDrawerEnabled = this.publicMenu;
        }
        // Order the menu.
        this.sortMenuItems(this.processedMenu);
        if (!this.appDrawerEnabled) {
            this.appDrawer?.close();
        }
        else {
        }
        return this.processedMenu;
    }
    /**
     * Normaliza roles ROLE_WILDCARD_PUBLIC recursivamente na arvore de menus.
     * @param item
     */
    normalizeRolesPublicItem(item) {
        // Recursividade para os filhos
        if (item.children && item.children.length > 0) {
            item.children.forEach((child) => {
                this.normalizeRolesPublicItem(child);
            });
        }
        // Regras de normalização
        item.roles = [ROLE_WILDCARD_PUBLIC];
    }
    /**
     * Normaliza roles ROLE_WILDCARD_UNRESTRICTED recursivamente na arvore de menus.
     * @param item
     */
    normalizeRolesUnrestrictedItem(item) {
        // Recursividade para os filhos
        if (item.children && item.children.length > 0) {
            item.children.forEach((child) => {
                this.normalizeRolesUnrestrictedItem(child);
            });
        }
        // Regras de normalização
        item.roles = [ROLE_WILDCARD_ONLYUSERS];
    }
    /**
     * Normaliza roles recursivamente na arvore de menus.
     * @param item
     * @param fatherRoles
     * @param isRoot
     */
    normalizeRolesRestrictedItem(item, fatherRoles, isRoot) {
        // Regras de normalização para filhos
        if (!isRoot) {
            // Remove roles que não existem no pai
            item.roles = item.roles.filter((role) => fatherRoles.includes(role));
            // Adiciona todas roles do pai se for vazio.
            if (item.roles.length === 0) {
                item.roles = fatherRoles;
            }
        }
        // Recursividade para os filhos
        if (item.children && item.children.length > 0) {
            item.children.forEach((child) => {
                this.normalizeRolesRestrictedItem(child, item.roles, false);
            });
        }
    }
    /**
     * Irá remover os itens que não possuem alguma role do usuário
     * E irá agrupar itens que são considerados duplicados
     *
     * @param rootRef - Referencia para o menu raiz
     * @param userRoles - Roles do usuário
     */
    groupAndDeleteMenuItems(rootRef = [], userRoles) {
        // Itera sobre os itens do menu
        let i = 0;
        for (i = 0; i < rootRef.length; i++) {
            let itemBeingChecked = rootRef[i];
            // Remove item se ele o usuário não possuir roles
            if (!itemBeingChecked.roles.includes(ROLE_WILDCARD_ONLYUSERS) &&
                !itemBeingChecked.roles.includes(ROLE_WILDCARD_PUBLIC)) {
                if (!userRoles.some((role) => itemBeingChecked.roles.includes(role))) {
                    rootRef.splice(i, 1);
                    i--; // Como o item foi removido, sua posição foi assumida pelo próximo item.
                    continue;
                }
            }
            // Itera sobre os próximos itens partindo do item anterior
            let j = 0;
            for (j = i + 1; j < rootRef.length; j++) {
                let item = rootRef[j];
                // Se o item sendo checado for igual ao item analisado, remove.
                if (itemBeingChecked.displayName === item.displayName &&
                    itemBeingChecked.iconName === item.iconName &&
                    itemBeingChecked.route === item.route) {
                    // Agrupa os itens
                    this.mergeMenuItem(itemBeingChecked, item);
                    // Remove a referencia duplicada
                    rootRef.splice(j, 1);
                    // Como o item foi removido, sua posição foi assumida pelo próximo item.
                    j--;
                }
            }
            // Se está no final da lista, não irá haver próxima iteração, faz a checagem dos filhos.
            if (j === rootRef.length) {
                this.groupAndDeleteMenuItems(itemBeingChecked.children, userRoles);
            }
        }
    }
    /**
     * Critério e Ação de agrupamento de dois menuItem
     * @param base
     * @param itemToBeMerged
     */
    mergeMenuItem(base, itemToBeMerged) {
        // concatena as roles
        base.roles = base.roles.concat(itemToBeMerged.roles);
        base.roles = [...new Set(base.roles)]; // remove roles duplicadas.
        // concatena os filhos
        if (base.children &&
            itemToBeMerged.children &&
            itemToBeMerged.children.length > 0) {
            base.children = base.children.concat(itemToBeMerged.children);
        }
    }
    sortMenuItems(siblings) {
        if (siblings && siblings.length > 1) {
            siblings.sort((a, b) => {
                if (a.order !== undefined && b.order !== undefined) {
                    if (a.order < b.order)
                        return -1;
                    if (a.order > b.order)
                        return 1;
                }
                return a.displayName < b.displayName ? -1 : 1;
            });
            siblings.forEach((item) => {
                this.sortMenuItems(item.children);
            });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMenuNavigationService, deps: [{ token: i3.Router }, { token: UnespCoreUserService }, { token: 'environment' }, { token: 'menu' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMenuNavigationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMenuNavigationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i3.Router }, { type: UnespCoreUserService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['menu']
                }] }] });

class UnespCoreMenuNavigationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMenuNavigationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMenuNavigationModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMenuNavigationModule, providers: [UnespCoreMenuNavigationService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMenuNavigationModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [UnespCoreMenuNavigationService]
                }]
        }] });

/**
 * @description
 *
 * Serviço responsável por exibir mensagens de sucesso/erro/informação para o usuário utilizando
 * o componente SnackBar do Angular Material.
 *
 */
class UnespCoreMessageService {
    constructor(snackBar) {
        this.snackBar = snackBar;
    }
    /**
     *
     * Exibe a mensagem de sucesso especificada para o usuário
     *
     */
    showMessageSuccess(msg, action = 'Fechar', duration) {
        this.showMessage(msg, action, 'success-snackbar', duration);
    }
    /**
     *
     * Exibe a mensagem de erro especificada para o usuário
     *
     */
    showMessageWarning(msg, action = 'Fechar', duration) {
        this.showMessage(msg, action, 'warning-snackbar', duration);
    }
    /**
     *
     * Exibe a mensagem de erro especificada para o usuário
     *
     */
    showMessageError(msg, action = 'Fechar', duration) {
        this.showMessage(msg, action, 'error-snackbar', duration);
    }
    /**
     *
     * Exibe múltiplas mensagens ao usuário agrupados por tipo e quantidade de mensagens por notificação.
     * O componente Snackbar somente exibe uma notificação por vez conforme especificação do Material.
     * Neste método apenas uma notificação será exibida por vez, porém cada notificação pode ter várias mensagens
     * de acordo com o que foi informado no parâmetro 'numberOfMessagesPerNotification' (Default 3).
     * Um delay de 500 milisegundos é utilizado entre as notificações (transição).
     * A notificação será exibida por 3 segundos por padrão, mas pode ser customizado pelo parâmetro 'duration'
     * Serão exibidos em ordem: Error, Warn, Info, Success (Ordem do ENUM)
     *
     */
    showMessagesGroupByType(msgs, action = 'Fechar', numberOfMessagesPerNotification = 3, duration = 3000) {
        // timeout between messages (transition)
        const timeout = 500;
        // index that control timeout between messages
        let index = 0;
        Object.values(UnespCoreMessageType).forEach(unespCoreMessageType => {
            const msgsGroupedByType = msgs.filter(message => message.type === unespCoreMessageType);
            let chunked = [];
            for (let i = 0; i < msgsGroupedByType.length; i += numberOfMessagesPerNotification) {
                chunked.push(msgsGroupedByType.slice(i, i + numberOfMessagesPerNotification));
            }
            chunked.forEach(mensagens => {
                let message = '';
                mensagens.forEach(currentValue => {
                    message += currentValue.message + '\n\n';
                });
                setTimeout(() => {
                    this.showMessage(message, action, mensagens[0].type, duration);
                }, index++ * (duration + timeout)); // timeout between messages
            });
        });
    }
    /**
     *
     * Exibe a mensagem informativa especificada para o usuário
     *
     */
    showMessageInfo(msg, action = 'Fechar', duration) {
        this.showMessage(msg, action, 'info-snackbar', duration);
    }
    /**
     *
     * Recebe a mensagem e monta o SnackBar para exibição ao usuário
     *
     */
    showMessage(msg, action = 'Fechar', panelClass, duration = 3000) {
        this.snackBar.open(msg, action, {
            duration: duration,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMessageService, deps: [{ token: i1.MatSnackBar }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMessageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMessageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.MatSnackBar }] });

class UnespCoreMessageModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMessageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMessageModule, imports: [MatSnackBarModule], exports: [MatSnackBarModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMessageModule, providers: [UnespCoreMessageService], imports: [MatSnackBarModule, MatSnackBarModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreMessageModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [UnespCoreMessageService],
                    imports: [
                        MatSnackBarModule
                    ],
                    exports: [
                        MatSnackBarModule
                    ]
                }]
        }] });

/**
 * @description
 *
 * Serviço responsável por se comunicar com a Central de Acessos e definir usuário para a aplicação.
 *
 * - Efetua autorização na central de acessos
 * - Aciona serviço UnespCoreUserService para armazenar o usuário autorizado
 * - Realiza verificações de permissões baseadas em roles
 */
class UnespCoreAuthService {
    get aquiredTokenTimeLeft$() {
        return this._aquiredTokenTimeLeft$;
    }
    constructor(http, unespCoreUserService, router, unespCoreMenuNavigationService, unespCoreMessageService, environment) {
        this.http = http;
        this.unespCoreUserService = unespCoreUserService;
        this.router = router;
        this.unespCoreMenuNavigationService = unespCoreMenuNavigationService;
        this.unespCoreMessageService = unespCoreMessageService;
        this.environment = environment;
        this._aquiredTokenTimeLeft$ = new BehaviorSubject(null);
        this.clientId = environment.clientId;
        this.redirectUri = environment.baseUrlRedirect;
        this.authServer = environment.oauthAuthorize;
        this.resourceServer = environment.baseUrlApi;
        this.refreshTokenExpirationInSeconds = environment.refreshTokenExpirationInSeconds;
        this.tokenDateKey = this.unespCoreUserService.getLocalStoragePrefix() + '_tokendate';
    }
    /**
     * Realiza autorização na central de acesso.
     *
     * @param {string} code Authorization Code obtido na central de acessos.
     */
    retrieveToken(code) {
        const authorizationCode = {
            code,
        };
        this.http
            .post(`${this.resourceServer}/auth`, authorizationCode)
            .subscribe((usuario) => {
            // set roles
            const authorities = usuario?.authorities;
            const roles = Object.entries(this.environment.roles).filter((role) => {
                return authorities?.indexOf(role[1].name) !== -1;
            });
            usuario.roles = roles.map((role) => role[0]);
            usuario.roles.push(ROLE_WILDCARD_PUBLIC);
            usuario.roles.push(ROLE_WILDCARD_ONLYUSERS);
            this.renewAquiredTokenTimeleft();
            this.unespCoreMessageService.showMessageSuccess('Usuário autenticado com sucesso');
            this.unespCoreUserService.setUser(usuario);
            this.unespCoreMenuNavigationService.refreshMenu();
        }, (err) => {
            this.unespCoreMessageService.showMessageError('Ocorreu um erro durante a autenticação: ' + err);
        });
    }
    /**
     * Realiza logout do usuário na aplicação. O backend é acionado para remoção dos cookies de sessão do qual
     * o javascript não tem acesso.
     *
     * @param {string} mensagem Mensagem a ser exibida ao usuário.
     * @param {boolean} error Indica se a mensagem representa um erro ou não.
     */
    logout(mensagem, error = false) {
        this.http.get(`${this.resourceServer}/auth/logout`).subscribe(() => {
            if (mensagem) {
                if (!error) {
                    this.unespCoreMessageService.showMessageSuccess(mensagem);
                }
                else {
                    this.unespCoreMessageService.showMessageError(mensagem);
                }
            }
        }, (err) => this.unespCoreMessageService.showMessageError('Ocorreu um problema para efetuar logout no backend'), () => {
            this.eraseAquiredTokenTimeleft();
            this.unespCoreUserService.removeUser();
            this.router.navigate(['']);
            this.unespCoreMenuNavigationService.refreshMenu();
        });
    }
    /**
     * Verifica se o usuário tem alguma das roles presentes na lista de roles autorizadas enviadas no parâmetro rolesRoute
     *
     * @param { UnespCoreRolesRoute } rolesRoute Lista de roles autorizadas a ser comparada com as roles do usuário
     */
    checkAuthorizedRole(rolesRoute) {
        const usuario = this.unespCoreUserService.getUser();
        // Role public, route não precisa de usuário
        if (rolesRoute.allowedRoles.includes(ROLE_WILDCARD_PUBLIC))
            return true;
        // Só avança se houver usuário logado
        if (!usuario || !usuario.authorities) {
            return false;
        }
        // Role Only Users, route precisa de usuário (mesmo sem roles)
        if (rolesRoute.allowedRoles.includes(ROLE_WILDCARD_ONLYUSERS))
            return true;
        // Verifica se o usuário tem alguma das roles autorizadas
        return usuario.authorities.some((authority) => rolesRoute.allowedRoles.includes(authority));
    }
    /**
     * Verifica se o usuário tem alguma das roles presentes na lista de roles autorizadas enviadas no parâmetro requiredRoles
     *
     * @param { string[] } requiredRoles Lista de roles autorizadas a ser comparada com as roles do usuário
     */
    hasPermissionBasedRoles(requiredRoles) {
        const usuario = this.unespCoreUserService.getUser();
        if (usuario) {
            return usuario.roles.some((role) => requiredRoles.indexOf(role) !== -1);
        }
        return false;
    }
    /**
     * Aciona API para refresh token
     */
    refreshToken() {
        return this.http.get(`${this.resourceServer}/auth/refresh-token`);
    }
    /**
     * Gera a URL de Login
     */
    getLoginUrl() {
        return `${this.authServer}?response_type=code&scope=openid&client_id=${this.clientId}&redirect_uri=${this.redirectUri}`;
    }
    renewAquiredTokenTimeleft() {
        localStorage.setItem(this.tokenDateKey, Math.round(Date.now()).toString());
        this._aquiredTokenTimeLeft$.next(this.refreshTokenExpirationInSeconds || 0);
    }
    eraseAquiredTokenTimeleft() {
        this._aquiredTokenTimeLeft$.next(0);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthService, deps: [{ token: i1$1.HttpClient }, { token: UnespCoreUserService }, { token: i3.Router }, { token: UnespCoreMenuNavigationService }, { token: UnespCoreMessageService }, { token: 'environment' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$1.HttpClient }, { type: UnespCoreUserService }, { type: i3.Router }, { type: UnespCoreMenuNavigationService }, { type: UnespCoreMessageService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }] });

/**
 * @description
 *
 * Serviço responsável por interceptar as mensagens de erros do backend
 *
 * - Erro 500: Emite mensagem de problema interno no servidor
 * - Outros erros tratados pelo backend Spring Boot são exibidos por este serviço de forma integrada,
 * - Indisponibilidade do backend: Exibe mensagem para usuário tentar novamente em alguns segundos
 *
 */
class UnespCoreApiErrorHandlerService {
    constructor(unespCoreMessageService) {
        this.unespCoreMessageService = unespCoreMessageService;
    }
    handle(errorResponse) {
        // Erro interno no backend
        if (errorResponse.status === '500') {
            this.unespCoreMessageService.showMessageError('Ocorreu um problema interno do servidor');
            return;
        }
        // Captura mensagem de erro retornado pela API
        if (errorResponse.error && errorResponse.error[0] !== undefined) {
            this.unespCoreMessageService.showMessageError(errorResponse.error[0].mensagemUsuario);
            console.error(errorResponse.error[0].mensagemDesenvolvedor);
            return;
        }
        // Erro genérico - Backend indisponível
        this.unespCoreMessageService.showMessageError('Ocorreu um problema, aguarde alguns instantes e tente novamente.');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreApiErrorHandlerService, deps: [{ token: UnespCoreMessageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreApiErrorHandlerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreApiErrorHandlerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UnespCoreMessageService }] });

/**
 * @description
 *
 * Módulo do serviço `UnespCoreApiErrorHandlerService`.
 */
class UnespCoreApiErrorHandlerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreApiErrorHandlerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreApiErrorHandlerModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreApiErrorHandlerModule, providers: [UnespCoreApiErrorHandlerService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreApiErrorHandlerModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [UnespCoreApiErrorHandlerService]
                }]
        }] });

/**
 * @description
 *
 * Módulo do serviço `UnespCoreAuthService`.
 */
class UnespCoreAuthModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthModule, providers: [UnespCoreAuthService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [UnespCoreAuthService]
                }]
        }] });

/**
 * @description
 *
 * Serviço responsável por proteger rotas baseado em roles específicas
 *
 */
class UnespCoreAuthGuardService {
    constructor(unespCoreAuthService, router) {
        this.unespCoreAuthService = unespCoreAuthService;
        this.router = router;
    }
    canActivate(route, state) {
        const unespCoreRolesRoute = {
            allowedRoles: route.data.allowedRoles
        };
        const isAuthorized = this.unespCoreAuthService.checkAuthorizedRole(unespCoreRolesRoute);
        if (!isAuthorized) {
            this.router.navigate(['access-denied']);
        }
        return isAuthorized;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthGuardService, deps: [{ token: UnespCoreAuthService }, { token: i3.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UnespCoreAuthService }, { type: i3.Router }] });

class UnespCoreAuthGuardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthGuardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthGuardModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthGuardModule, providers: [UnespCoreAuthGuardService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthGuardModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [UnespCoreAuthGuardService]
                }]
        }] });

/**
 * @description
 *
 * Serviço responsável por disponibilizar de maneira genérica o acesso a APIs paginadas ou não, métodos CRUD como
 * create(POST), update(PUT), delete(DELETE).
 * É possível extender este serviço e reaproveitar seus métodos para uso específico.
 *
 */
class UnespCoreGenericService {
    constructor(http, environment) {
        this.http = http;
        this.ENDPOINT = environment.baseUrlApi;
    }
    /** Retorna Lista Paginada de dados <T> */
    fetchPagination(route, p) {
        return this.http
            .get(`${this.ENDPOINT}/${route}`, { params: p })
            .pipe(map((data) => data));
    }
    /** Retorna uma Lista de Dados <T> sem paginação */
    fetchWithoutPagination(route, p) {
        return this.http
            .get(`${this.ENDPOINT}/${route}`, { params: p })
            .pipe(map((data) => data));
    }
    /** Retorna Item unico de <T> */
    findById(route, id) {
        return this.http
            .get(`${this.ENDPOINT}/${route}/${id}`)
            .pipe(map((data) => data));
    }
    /** Retorna Item de <T> */
    get(route) {
        return this.http
            .get(`${this.ENDPOINT}/${route}`)
            .pipe(map((data) => data));
    }
    /** Adiciona recurso */
    create(route, input) {
        return this.http
            .post(`${this.ENDPOINT}/${route}`, input)
            .pipe(map((obj) => obj));
    }
    /** Atualiza recurso */
    update(uri, input) {
        const url = `${this.ENDPOINT}/${uri}`;
        return this.http.put(url, input).pipe(map((obj) => obj));
    }
    /** Remove recurso */
    delete(route, id) {
        const url = `${this.ENDPOINT}/${route}/${id}`;
        return this.http.delete(url).pipe(map((obj) => obj));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreGenericService, deps: [{ token: i1$1.HttpClient }, { token: 'environment' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreGenericService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreGenericService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }] });

class UnespCoreGenericServiceModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreGenericServiceModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreGenericServiceModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreGenericServiceModule, providers: [UnespCoreGenericService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreGenericServiceModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [UnespCoreGenericService]
                }]
        }] });

/**
 * @description
 *
 * Serviço responsável pelo controle de carregamento no acesso a uma API
 *
 */
class UnespCoreLoaderService {
    constructor() {
        this.isLoading = new BehaviorSubject(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class UnespCoreLoaderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderModule, providers: [UnespCoreLoaderService] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [UnespCoreLoaderService]
                }]
        }] });

class UnespCoreServicesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreServicesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreServicesModule, imports: [UnespCoreApiErrorHandlerModule,
            UnespCoreAuthModule,
            UnespCoreAuthGuardModule,
            UnespCoreUserModule,
            UnespCoreGenericServiceModule,
            UnespCoreLoaderModule,
            UnespCoreMenuNavigationModule,
            UnespCoreMessageModule], exports: [UnespCoreApiErrorHandlerModule,
            UnespCoreAuthModule,
            UnespCoreAuthGuardModule,
            UnespCoreUserModule,
            UnespCoreGenericServiceModule,
            UnespCoreLoaderModule,
            UnespCoreMenuNavigationModule,
            UnespCoreMessageModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreServicesModule, imports: [UnespCoreApiErrorHandlerModule,
            UnespCoreAuthModule,
            UnespCoreAuthGuardModule,
            UnespCoreUserModule,
            UnespCoreGenericServiceModule,
            UnespCoreLoaderModule,
            UnespCoreMenuNavigationModule,
            UnespCoreMessageModule, UnespCoreApiErrorHandlerModule,
            UnespCoreAuthModule,
            UnespCoreAuthGuardModule,
            UnespCoreUserModule,
            UnespCoreGenericServiceModule,
            UnespCoreLoaderModule,
            UnespCoreMenuNavigationModule,
            UnespCoreMessageModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreServicesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        UnespCoreApiErrorHandlerModule,
                        UnespCoreAuthModule,
                        UnespCoreAuthGuardModule,
                        UnespCoreUserModule,
                        UnespCoreGenericServiceModule,
                        UnespCoreLoaderModule,
                        UnespCoreMenuNavigationModule,
                        UnespCoreMessageModule
                    ],
                    exports: [
                        UnespCoreApiErrorHandlerModule,
                        UnespCoreAuthModule,
                        UnespCoreAuthGuardModule,
                        UnespCoreUserModule,
                        UnespCoreGenericServiceModule,
                        UnespCoreLoaderModule,
                        UnespCoreMenuNavigationModule,
                        UnespCoreMessageModule
                    ],
                    providers: [],
                }]
        }] });

class UnespCoreHeaderCountdownComponent {
    constructor(unespCoreAuthService, unespCoreUserService, unespCoreMessageService, environment) {
        this.unespCoreAuthService = unespCoreAuthService;
        this.unespCoreUserService = unespCoreUserService;
        this.unespCoreMessageService = unespCoreMessageService;
        this.environment = environment;
        this.config = {
            leftTime: 0,
            notify: 0,
            format: 'm:ss',
        };
        this.warningStickyTime = 10000;
        this.warningNotifications = undefined;
        this.lastWarningNotification = undefined;
        this.timerKey = 'time';
        if (!environment.refreshTokenExpirationInSeconds || !this.unespCoreAuthService.refreshTokenExpirationInSeconds) {
            throw new Error('environment.refreshTokenExpirationInSeconds precisa estar definido!');
        }
        this.warningNotifications = environment.refreshTokenExpirationActivityNotificationsTime;
        this.lastWarningNotification = environment.refreshTokenExpirationLastWarningTime;
        this.timerKey = this.unespCoreUserService.getLocalStoragePrefix() + '_time';
    }
    ngOnInit() {
        let value = 0;
        // Atualiza o timer de acordo com o tokendate
        if (localStorage.getItem(this.unespCoreAuthService.tokenDateKey)) {
            const tokenDateMillis = +localStorage.getItem(this.unespCoreAuthService.tokenDateKey);
            const nowMillings = Math.round(Date.now());
            const secondsElapsed = Math.floor((nowMillings - tokenDateMillis) / 1000);
            const secondsLeft = this.unespCoreAuthService.refreshTokenExpirationInSeconds - secondsElapsed;
            value = secondsLeft;
        }
        else {
            // Atualiza o timer de acordo com o localstorage ou authService
            const localStorageTimer = localStorage.getItem(this.timerKey);
            value = localStorageTimer ? +localStorageTimer : this.unespCoreAuthService.refreshTokenExpirationInSeconds;
        }
        value--; // init offset, for loading/reloading time
        if (value <= 0) {
            // value = this.unespCoreAuthService.refreshTokenExpirationInSeconds || 0;
            value = 0;
        }
        // atualiza a config do componente.
        this.config = { ...this.config, leftTime: value };
        this.unespCoreAuthService.aquiredTokenTimeLeft$.subscribe((timeLeft) => {
            if (timeLeft) {
                localStorage.setItem(this.timerKey, `${timeLeft}`);
                this.config = { ...this.config, leftTime: timeLeft };
                this.startTimer();
            }
        });
    }
    startTimer() {
        if (this.timer) {
            if (this.timer.left > 0) {
                this.timer.restart();
            }
            else {
                this.timer.begin();
            }
        }
    }
    handleEvent(e) {
        let secondsLeft = e.left / 1000;
        if (e.action === 'notify') {
            // Save current value
            localStorage.setItem(this.timerKey, `${secondsLeft}`);
            if (this.warningNotifications) {
                if (this.warningNotifications.includes(secondsLeft)) {
                    this.showWarning();
                }
            }
            if (this.lastWarningNotification) {
                if (this.lastWarningNotification == secondsLeft) {
                    this.showLastWarning(secondsLeft);
                }
            }
        }
        if (secondsLeft === 3)
            this.showDialogWarning_lost();
        if (e.action === 'done') {
            this.showDialogWarning_lost();
            this.changeCounterToExpired();
        }
    }
    showWarning() {
        this.unespCoreMessageService.showMessageWarning(`Olá, você está aí? Lembre-se de salvar seus dados!`, undefined, this.warningStickyTime);
    }
    showLastWarning(timeLeft) {
        this.showDialogWarning_almost();
        let strTime = '';
        if (timeLeft < 60)
            strTime = ' em ' + timeLeft.toString() + ' segundos';
        else {
            let minutes = Math.floor(timeLeft / 60);
            strTime = ' em ' + minutes.toString() + ' minuto';
            if (minutes > 1)
                strTime += 's';
        }
        this.unespCoreMessageService.showMessageError(`Sua autorização para utilizar o sistema irá expirar${strTime}.`, undefined, this.warningStickyTime * 2);
    }
    showDialogWarning_almost() {
        this.dialogwarning.nativeElement.setAttribute('style', 'visibility: visible;');
        this.dialogwarning_almost.nativeElement.setAttribute('style', 'display: block;');
        this.dialogwarning_lost.nativeElement.setAttribute('style', 'display: none;');
        this.countdowndiv.nativeElement.setAttribute('style', 'color: red; position:absolute; z-index: 999999; font-size: xx-large; top: 5px; right: 5px;');
        this.countdowndiv_expiredmsg.nativeElement.setAttribute('style', 'display: none;');
    }
    showDialogWarning_lost() {
        this.dialogwarning.nativeElement.setAttribute('style', 'visibility: visible;');
        this.dialogwarning_almost.nativeElement.setAttribute('style', 'display: none;');
        this.dialogwarning_lost.nativeElement.setAttribute('style', 'display: block;');
        this.countdowndiv.nativeElement.setAttribute('style', 'color: red; position:absolute; z-index: 999999; font-size: xx-large; top: 5px; right: 5px;');
    }
    changeCounterToExpired() {
        this.countdowndiv_expiredmsg.nativeElement.setAttribute('style', 'display: block;');
    }
    hideDialogWarning() {
        this.dialogwarning.nativeElement.setAttribute('style', 'visibility: hidden;');
        this.dialogwarning_almost.nativeElement.setAttribute('style', 'display: none;');
        this.dialogwarning_lost.nativeElement.setAttribute('style', 'display: none;');
        this.countdowndiv.nativeElement.setAttribute('style', '');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderCountdownComponent, deps: [{ token: UnespCoreAuthService }, { token: UnespCoreUserService }, { token: UnespCoreMessageService }, { token: 'environment' }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.7", type: UnespCoreHeaderCountdownComponent, isStandalone: false, selector: "unesp-core-header-countdown", viewQueries: [{ propertyName: "timer", first: true, predicate: ["timer"], descendants: true }, { propertyName: "countdowndiv", first: true, predicate: ["countdowndiv"], descendants: true }, { propertyName: "countdowndiv_expiredmsg", first: true, predicate: ["countdowndiv_expiredmsg"], descendants: true }, { propertyName: "dialogwarning", first: true, predicate: ["dialogwarning"], descendants: true }, { propertyName: "dialogwarning_almost", first: true, predicate: ["dialogwarningtext_almost"], descendants: true }, { propertyName: "dialogwarning_lost", first: true, predicate: ["dialogwarningtext_lost"], descendants: true }], ngImport: i0, template: "<div fxFlexLayout=\"row\" fxLayoutGap=\"10px\" fxLayoutAlign=\"center center\">\n  <div fxFlex #countdowndiv_expiredmsg class=\"unesp-header-countdown-expiredmsg\" style=\"display: none;\">\n    Sess\u00E3o expirada!\n  </div>\n  <div fxFlex #countdowndiv class=\"unesp-header-countdown\">\n    <countdown #timer [config]=\"config\" (event)=\"handleEvent($event)\"></countdown>\n  </div>\n</div>\n<div #dialogwarning class=\"unesp-header-countdown-dialog-lastwarning\" style=\"visibility: hidden;\">\n  <div class=\"unesp-header-countdown-dialog-lastwarning-content\">\n    <div class=\"unesp-header-countdown-dialog-lastwarning-content-title\">\n      Aten\u00E7\u00E3o\n    </div>\n    <div #dialogwarningtext_almost class=\"unesp-header-countdown-dialog-lastwarning-content-text\" style=\"visibility: hidden;\">\n      <p>\n        Voc\u00EA est\u00E1 prestes a perder sua autoriza\u00E7\u00E3o para continuar utilizando o sistema.<br/>\n        Caso tenha dados pendentes no sistema, salve-os imediatamente.<br/>\n      </p>\n    </div>\n    <div #dialogwarningtext_lost class=\"unesp-header-countdown-dialog-lastwarning-content-text\" style=\"visibility: hidden;\">\n      <p>\n        Voc\u00EA perdeu sua autoriza\u00E7\u00E3o para continuar utilizando o sistema.<br/>\n        Caso tenha dados pendentes no sistema, salve-os em um local separado antes de prosseguir no sistema.<br/>\n      </p>\n    </div>\n    <button mat-raised-button color=\"primary\" (click)=\"hideDialogWarning()\">\n      Fechar este aviso\n    </button>\n  </div>\n</div>", styles: [".unesp-header-countdown{font-size:small}.unesp-header-countdown-expiredmsg{color:red;background-color:#00085bd9;padding:5px}.unesp-header-countdown-dialog-lastwarning{position:absolute;top:0;left:0;width:100%;height:100%;background-color:#000000f2;z-index:999;text-align:center;display:flex;align-items:center}.unesp-header-countdown-dialog-lastwarning-content{width:100%}.unesp-header-countdown-dialog-lastwarning-content-title{color:red;font-size:xx-large;font-weight:700}.unesp-header-countdown-dialog-lastwarning-content-text{margin:40px 0;white-space:normal}\n"], dependencies: [{ kind: "component", type: i2.CountdownComponent, selector: "countdown", inputs: ["config", "render"], outputs: ["event"] }, { kind: "component", type: i7.MatButton, selector: "    button[mat-button], button[mat-raised-button], button[mat-flat-button],    button[mat-stroked-button]  ", exportAs: ["matButton"] }, { kind: "directive", type: i1$2.DefaultLayoutGapDirective, selector: "  [fxLayoutGap], [fxLayoutGap.xs], [fxLayoutGap.sm], [fxLayoutGap.md],  [fxLayoutGap.lg], [fxLayoutGap.xl], [fxLayoutGap.lt-sm], [fxLayoutGap.lt-md],  [fxLayoutGap.lt-lg], [fxLayoutGap.lt-xl], [fxLayoutGap.gt-xs], [fxLayoutGap.gt-sm],  [fxLayoutGap.gt-md], [fxLayoutGap.gt-lg]", inputs: ["fxLayoutGap", "fxLayoutGap.xs", "fxLayoutGap.sm", "fxLayoutGap.md", "fxLayoutGap.lg", "fxLayoutGap.xl", "fxLayoutGap.lt-sm", "fxLayoutGap.lt-md", "fxLayoutGap.lt-lg", "fxLayoutGap.lt-xl", "fxLayoutGap.gt-xs", "fxLayoutGap.gt-sm", "fxLayoutGap.gt-md", "fxLayoutGap.gt-lg"] }, { kind: "directive", type: i1$2.DefaultLayoutAlignDirective, selector: "  [fxLayoutAlign], [fxLayoutAlign.xs], [fxLayoutAlign.sm], [fxLayoutAlign.md],  [fxLayoutAlign.lg], [fxLayoutAlign.xl], [fxLayoutAlign.lt-sm], [fxLayoutAlign.lt-md],  [fxLayoutAlign.lt-lg], [fxLayoutAlign.lt-xl], [fxLayoutAlign.gt-xs], [fxLayoutAlign.gt-sm],  [fxLayoutAlign.gt-md], [fxLayoutAlign.gt-lg]", inputs: ["fxLayoutAlign", "fxLayoutAlign.xs", "fxLayoutAlign.sm", "fxLayoutAlign.md", "fxLayoutAlign.lg", "fxLayoutAlign.xl", "fxLayoutAlign.lt-sm", "fxLayoutAlign.lt-md", "fxLayoutAlign.lt-lg", "fxLayoutAlign.lt-xl", "fxLayoutAlign.gt-xs", "fxLayoutAlign.gt-sm", "fxLayoutAlign.gt-md", "fxLayoutAlign.gt-lg"] }, { kind: "directive", type: i1$2.DefaultFlexDirective, selector: "  [fxFlex], [fxFlex.xs], [fxFlex.sm], [fxFlex.md],  [fxFlex.lg], [fxFlex.xl], [fxFlex.lt-sm], [fxFlex.lt-md],  [fxFlex.lt-lg], [fxFlex.lt-xl], [fxFlex.gt-xs], [fxFlex.gt-sm],  [fxFlex.gt-md], [fxFlex.gt-lg]", inputs: ["fxFlex", "fxFlex.xs", "fxFlex.sm", "fxFlex.md", "fxFlex.lg", "fxFlex.xl", "fxFlex.lt-sm", "fxFlex.lt-md", "fxFlex.lt-lg", "fxFlex.lt-xl", "fxFlex.gt-xs", "fxFlex.gt-sm", "fxFlex.gt-md", "fxFlex.gt-lg"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderCountdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'unesp-core-header-countdown', standalone: false, template: "<div fxFlexLayout=\"row\" fxLayoutGap=\"10px\" fxLayoutAlign=\"center center\">\n  <div fxFlex #countdowndiv_expiredmsg class=\"unesp-header-countdown-expiredmsg\" style=\"display: none;\">\n    Sess\u00E3o expirada!\n  </div>\n  <div fxFlex #countdowndiv class=\"unesp-header-countdown\">\n    <countdown #timer [config]=\"config\" (event)=\"handleEvent($event)\"></countdown>\n  </div>\n</div>\n<div #dialogwarning class=\"unesp-header-countdown-dialog-lastwarning\" style=\"visibility: hidden;\">\n  <div class=\"unesp-header-countdown-dialog-lastwarning-content\">\n    <div class=\"unesp-header-countdown-dialog-lastwarning-content-title\">\n      Aten\u00E7\u00E3o\n    </div>\n    <div #dialogwarningtext_almost class=\"unesp-header-countdown-dialog-lastwarning-content-text\" style=\"visibility: hidden;\">\n      <p>\n        Voc\u00EA est\u00E1 prestes a perder sua autoriza\u00E7\u00E3o para continuar utilizando o sistema.<br/>\n        Caso tenha dados pendentes no sistema, salve-os imediatamente.<br/>\n      </p>\n    </div>\n    <div #dialogwarningtext_lost class=\"unesp-header-countdown-dialog-lastwarning-content-text\" style=\"visibility: hidden;\">\n      <p>\n        Voc\u00EA perdeu sua autoriza\u00E7\u00E3o para continuar utilizando o sistema.<br/>\n        Caso tenha dados pendentes no sistema, salve-os em um local separado antes de prosseguir no sistema.<br/>\n      </p>\n    </div>\n    <button mat-raised-button color=\"primary\" (click)=\"hideDialogWarning()\">\n      Fechar este aviso\n    </button>\n  </div>\n</div>", styles: [".unesp-header-countdown{font-size:small}.unesp-header-countdown-expiredmsg{color:red;background-color:#00085bd9;padding:5px}.unesp-header-countdown-dialog-lastwarning{position:absolute;top:0;left:0;width:100%;height:100%;background-color:#000000f2;z-index:999;text-align:center;display:flex;align-items:center}.unesp-header-countdown-dialog-lastwarning-content{width:100%}.unesp-header-countdown-dialog-lastwarning-content-title{color:red;font-size:xx-large;font-weight:700}.unesp-header-countdown-dialog-lastwarning-content-text{margin:40px 0;white-space:normal}\n"] }]
        }], ctorParameters: () => [{ type: UnespCoreAuthService }, { type: UnespCoreUserService }, { type: UnespCoreMessageService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }], propDecorators: { timer: [{
                type: ViewChild,
                args: ['timer']
            }], countdowndiv: [{
                type: ViewChild,
                args: ['countdowndiv']
            }], countdowndiv_expiredmsg: [{
                type: ViewChild,
                args: ['countdowndiv_expiredmsg']
            }], dialogwarning: [{
                type: ViewChild,
                args: ['dialogwarning']
            }], dialogwarning_almost: [{
                type: ViewChild,
                args: ['dialogwarningtext_almost']
            }], dialogwarning_lost: [{
                type: ViewChild,
                args: ['dialogwarningtext_lost']
            }] } });

/**
 * @description
 *
 * Este componente representa o cabeçalho da aplicação, disponibilizando o menu, login e logout.
 *
 */
class UnespCoreHeaderComponent {
    constructor(unespCoreMenuNavigationService, unespCoreAuthService, unespCoreUserService) {
        this.unespCoreMenuNavigationService = unespCoreMenuNavigationService;
        this.unespCoreAuthService = unespCoreAuthService;
        this.unespCoreUserService = unespCoreUserService;
        /**
         * @description
         *
         * Exibe username no cabeçalho ao lado do botão logout.
         *
         */
        this.usernameOnHeader = true;
        /**
         * @description
         *
         * Caminho do logotipo que será exibido
         *
         */
        this.logo = 'assets/img/logotipo.png';
        /**
         * @description
         *
         * Define se o botão do menu irá aparecer a todo momento.
         * Mesmo sem um usuário logado.
         *
         */
        this.publicMenu = true;
        /**
         * @description
         *
         * Define se adicionará e exibirá as funcionalidades do componente
         * de countdown timer no cabeçalho.
         */
        this.showCountdownTimer = true;
        /**
         * @description
         *
         * Define se exibirá o botão de login no header
         */
        this.loginOnHeader = false;
        /**
         * @description
         *
         * Define se exibirá o botão de logout no header
         */
        this.logoutOnHeader = true;
        /**
         * @description
         *
         * Define se exibirá o botão de fixar o menu no header
         */
        this.showPinMenu = false;
    }
    ngAfterViewInit() {
        this.unespCoreMenuNavigationService.publicMenu = this.publicMenu;
        this.unespCoreMenuNavigationService.refreshMenu();
    }
    logout() {
        this.unespCoreAuthService.logout('Usuário desconectado com sucesso');
        this.unespCoreMenuNavigationService.appDrawer?.close();
    }
    login() {
        window.location.href = this.unespCoreAuthService.getLoginUrl();
    }
    get user$() {
        return this.unespCoreUserService.user$;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderComponent, deps: [{ token: UnespCoreMenuNavigationService }, { token: UnespCoreAuthService }, { token: UnespCoreUserService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.7", type: UnespCoreHeaderComponent, isStandalone: false, selector: "unesp-core-header", inputs: { fullTitle: "fullTitle", shortTitle: "shortTitle", usernameOnHeader: "usernameOnHeader", logo: "logo", logoCustom: "logoCustom", publicMenu: "publicMenu", showCountdownTimer: "showCountdownTimer", loginOnHeader: "loginOnHeader", logoutOnHeader: "logoutOnHeader", showPinMenu: "showPinMenu" }, ngImport: i0, template: "<mat-toolbar color=\"primary\">\n  <mat-toolbar-row>\n    <div fxFlex=\"0 0 auto\" *ngIf=\"unespCoreMenuNavigationService.appDrawerEnabled || publicMenu\">\n      <button class=\"{{!showPinMenu? 'menu': ''}}\" mat-icon-button id=\"menu\" (click)=\"unespCoreMenuNavigationService.toggle()\" aria-label=\"menu\">\n        <mat-icon>menu</mat-icon>\n      </button>\n    </div>\n    <div fxFlex=\"0 0 auto\" *ngIf=\"(unespCoreMenuNavigationService.appDrawerEnabled || publicMenu)\n                      && showPinMenu && (unespCoreMenuNavigationService.appDrawer?.opened ?? false)\">\n      <button class=\"pinMenu\" mat-icon-button id=\"pinMenu\" (click)=\"unespCoreMenuNavigationService.togglePinMenu()\" aria-label=\"pin menu\">\n        <mat-icon *ngIf=\"!unespCoreMenuNavigationService.pinnedMenu\" svgIcon=\"keep\"></mat-icon>\n        <mat-icon *ngIf=\"unespCoreMenuNavigationService.pinnedMenu\" svgIcon=\"keep_off\"></mat-icon>\n      </button>\n    </div>\n    <div class=\"logo\" fxFlex=\"0 0 auto\" fxLayoutAlign=\"center\">\n      <img *ngIf=\"!logoCustom\" [src]=\"logo\" alt=\"\">\n      <img *ngIf=\"logoCustom\" [src]=\"logoCustom\" alt=\"\">\n    </div>\n    <div fxFlex=\"1 1 auto\" class=\"app-title\">\n      <span class=\"app-title-full\">{{ fullTitle }}</span>\n      <span class=\"app-title-short\">{{ shortTitle }}</span>\n    </div>\n    <div fxFlex=\"0 0 auto\" fxLayout=\"row\" fxLayoutAlign=\"center center\">\n      <div fxFlex=\"0 0 auto\" fxLayout=\"row\" *ngIf=\"showCountdownTimer && (user$ | async)\">\n        <unesp-core-header-countdown></unesp-core-header-countdown>\n      </div>\n      <div fxFlex=\"0 0 auto\" *ngIf=\"usernameOnHeader\">\n        <span class=\"usernameHeader\">{{(user$ | async)?.identificacao}}</span>\n      </div>\n    </div>\n    <div fxFlex=\"0 0 auto\">\n      <button class=\"app-login-logout\" *ngIf=\"logoutOnHeader && (user$ | async)\" mat-icon-button (click)=\"logout()\" fxFlex=\"1 1 0%\" aria-label=\"logout\">\n        <mat-icon>cancel</mat-icon>\n      </button>\n      <button class=\"app-login-logout\" *ngIf=\"loginOnHeader && !(user$ | async)\" mat-icon-button (click)=\"login()\" fxFlex=\"1 1 0%\" aria-label=\"login\">\n        <mat-icon>login</mat-icon>\n      </button>\n    </div>\n  </mat-toolbar-row>\n</mat-toolbar>\n", styles: [".mat-toolbar{min-height:inherit!important;height:inherit!important}.mat-toolbar-row{min-height:inherit!important;height:inherit!important;padding:2px 10px!important}.app-title{padding-left:15px}.menu,.pinMenu{margin-right:10px}.mat-toolbar-row .logo img{height:30px}.usernameHeader{margin-left:10px}@media (max-width: 1280px){.pinMenu{display:none}}@media (max-width: 850px){.app-title-full{white-space:normal;font-size:2.5vw}.app-title{padding-left:5px;line-height:1.8vh}.usernameHeader{font-size:2.5vw}}@media (max-width: 600px){.app-title-full{display:none}.usernameHeader{font-size:.7em}}@media (max-width: 300px),(min-width: 600px){.app-title-short{display:none}}@media (max-width: 420px){.app-login-logout,.usernameHeader{display:none}}\n"], dependencies: [{ kind: "directive", type: i3$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i5.MatToolbar, selector: "mat-toolbar", inputs: ["color"], exportAs: ["matToolbar"] }, { kind: "directive", type: i5.MatToolbarRow, selector: "mat-toolbar-row", exportAs: ["matToolbarRow"] }, { kind: "component", type: i3$2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "component", type: i7.MatIconButton, selector: "button[mat-icon-button]", exportAs: ["matButton"] }, { kind: "directive", type: i1$2.DefaultLayoutDirective, selector: "  [fxLayout], [fxLayout.xs], [fxLayout.sm], [fxLayout.md],  [fxLayout.lg], [fxLayout.xl], [fxLayout.lt-sm], [fxLayout.lt-md],  [fxLayout.lt-lg], [fxLayout.lt-xl], [fxLayout.gt-xs], [fxLayout.gt-sm],  [fxLayout.gt-md], [fxLayout.gt-lg]", inputs: ["fxLayout", "fxLayout.xs", "fxLayout.sm", "fxLayout.md", "fxLayout.lg", "fxLayout.xl", "fxLayout.lt-sm", "fxLayout.lt-md", "fxLayout.lt-lg", "fxLayout.lt-xl", "fxLayout.gt-xs", "fxLayout.gt-sm", "fxLayout.gt-md", "fxLayout.gt-lg"] }, { kind: "directive", type: i1$2.DefaultLayoutAlignDirective, selector: "  [fxLayoutAlign], [fxLayoutAlign.xs], [fxLayoutAlign.sm], [fxLayoutAlign.md],  [fxLayoutAlign.lg], [fxLayoutAlign.xl], [fxLayoutAlign.lt-sm], [fxLayoutAlign.lt-md],  [fxLayoutAlign.lt-lg], [fxLayoutAlign.lt-xl], [fxLayoutAlign.gt-xs], [fxLayoutAlign.gt-sm],  [fxLayoutAlign.gt-md], [fxLayoutAlign.gt-lg]", inputs: ["fxLayoutAlign", "fxLayoutAlign.xs", "fxLayoutAlign.sm", "fxLayoutAlign.md", "fxLayoutAlign.lg", "fxLayoutAlign.xl", "fxLayoutAlign.lt-sm", "fxLayoutAlign.lt-md", "fxLayoutAlign.lt-lg", "fxLayoutAlign.lt-xl", "fxLayoutAlign.gt-xs", "fxLayoutAlign.gt-sm", "fxLayoutAlign.gt-md", "fxLayoutAlign.gt-lg"] }, { kind: "directive", type: i1$2.DefaultFlexDirective, selector: "  [fxFlex], [fxFlex.xs], [fxFlex.sm], [fxFlex.md],  [fxFlex.lg], [fxFlex.xl], [fxFlex.lt-sm], [fxFlex.lt-md],  [fxFlex.lt-lg], [fxFlex.lt-xl], [fxFlex.gt-xs], [fxFlex.gt-sm],  [fxFlex.gt-md], [fxFlex.gt-lg]", inputs: ["fxFlex", "fxFlex.xs", "fxFlex.sm", "fxFlex.md", "fxFlex.lg", "fxFlex.xl", "fxFlex.lt-sm", "fxFlex.lt-md", "fxFlex.lt-lg", "fxFlex.lt-xl", "fxFlex.gt-xs", "fxFlex.gt-sm", "fxFlex.gt-md", "fxFlex.gt-lg"] }, { kind: "component", type: UnespCoreHeaderCountdownComponent, selector: "unesp-core-header-countdown" }, { kind: "pipe", type: i3$1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'unesp-core-header', standalone: false, template: "<mat-toolbar color=\"primary\">\n  <mat-toolbar-row>\n    <div fxFlex=\"0 0 auto\" *ngIf=\"unespCoreMenuNavigationService.appDrawerEnabled || publicMenu\">\n      <button class=\"{{!showPinMenu? 'menu': ''}}\" mat-icon-button id=\"menu\" (click)=\"unespCoreMenuNavigationService.toggle()\" aria-label=\"menu\">\n        <mat-icon>menu</mat-icon>\n      </button>\n    </div>\n    <div fxFlex=\"0 0 auto\" *ngIf=\"(unespCoreMenuNavigationService.appDrawerEnabled || publicMenu)\n                      && showPinMenu && (unespCoreMenuNavigationService.appDrawer?.opened ?? false)\">\n      <button class=\"pinMenu\" mat-icon-button id=\"pinMenu\" (click)=\"unespCoreMenuNavigationService.togglePinMenu()\" aria-label=\"pin menu\">\n        <mat-icon *ngIf=\"!unespCoreMenuNavigationService.pinnedMenu\" svgIcon=\"keep\"></mat-icon>\n        <mat-icon *ngIf=\"unespCoreMenuNavigationService.pinnedMenu\" svgIcon=\"keep_off\"></mat-icon>\n      </button>\n    </div>\n    <div class=\"logo\" fxFlex=\"0 0 auto\" fxLayoutAlign=\"center\">\n      <img *ngIf=\"!logoCustom\" [src]=\"logo\" alt=\"\">\n      <img *ngIf=\"logoCustom\" [src]=\"logoCustom\" alt=\"\">\n    </div>\n    <div fxFlex=\"1 1 auto\" class=\"app-title\">\n      <span class=\"app-title-full\">{{ fullTitle }}</span>\n      <span class=\"app-title-short\">{{ shortTitle }}</span>\n    </div>\n    <div fxFlex=\"0 0 auto\" fxLayout=\"row\" fxLayoutAlign=\"center center\">\n      <div fxFlex=\"0 0 auto\" fxLayout=\"row\" *ngIf=\"showCountdownTimer && (user$ | async)\">\n        <unesp-core-header-countdown></unesp-core-header-countdown>\n      </div>\n      <div fxFlex=\"0 0 auto\" *ngIf=\"usernameOnHeader\">\n        <span class=\"usernameHeader\">{{(user$ | async)?.identificacao}}</span>\n      </div>\n    </div>\n    <div fxFlex=\"0 0 auto\">\n      <button class=\"app-login-logout\" *ngIf=\"logoutOnHeader && (user$ | async)\" mat-icon-button (click)=\"logout()\" fxFlex=\"1 1 0%\" aria-label=\"logout\">\n        <mat-icon>cancel</mat-icon>\n      </button>\n      <button class=\"app-login-logout\" *ngIf=\"loginOnHeader && !(user$ | async)\" mat-icon-button (click)=\"login()\" fxFlex=\"1 1 0%\" aria-label=\"login\">\n        <mat-icon>login</mat-icon>\n      </button>\n    </div>\n  </mat-toolbar-row>\n</mat-toolbar>\n", styles: [".mat-toolbar{min-height:inherit!important;height:inherit!important}.mat-toolbar-row{min-height:inherit!important;height:inherit!important;padding:2px 10px!important}.app-title{padding-left:15px}.menu,.pinMenu{margin-right:10px}.mat-toolbar-row .logo img{height:30px}.usernameHeader{margin-left:10px}@media (max-width: 1280px){.pinMenu{display:none}}@media (max-width: 850px){.app-title-full{white-space:normal;font-size:2.5vw}.app-title{padding-left:5px;line-height:1.8vh}.usernameHeader{font-size:2.5vw}}@media (max-width: 600px){.app-title-full{display:none}.usernameHeader{font-size:.7em}}@media (max-width: 300px),(min-width: 600px){.app-title-short{display:none}}@media (max-width: 420px){.app-login-logout,.usernameHeader{display:none}}\n"] }]
        }], ctorParameters: () => [{ type: UnespCoreMenuNavigationService }, { type: UnespCoreAuthService }, { type: UnespCoreUserService }], propDecorators: { fullTitle: [{
                type: Input
            }], shortTitle: [{
                type: Input
            }], usernameOnHeader: [{
                type: Input
            }], logo: [{
                type: Input
            }], logoCustom: [{
                type: Input
            }], publicMenu: [{
                type: Input
            }], showCountdownTimer: [{
                type: Input
            }], loginOnHeader: [{
                type: Input
            }], logoutOnHeader: [{
                type: Input
            }], showPinMenu: [{
                type: Input
            }] } });

/**
 * @description
 *
 * Este componente representa os itens de menu da aplicação.
 *
 */
class UnespCoreSidenavItemComponent {
    constructor(unespCoreMenuNavigationService, router) {
        this.unespCoreMenuNavigationService = unespCoreMenuNavigationService;
        this.router = router;
        if (this.depth === undefined) {
            this.depth = 0;
        }
    }
    ngOnInit() {
        this.unespCoreMenuNavigationService.currentUrl.subscribe((url) => {
            if (this.item.route && url) {
                this.expanded = url.indexOf(`/${this.item.route}`) === 0;
                this.ariaExpanded = this.expanded;
            }
        });
    }
    onItemSelected(item) {
        if (!item.children || !item.children.length) {
            this.router.navigate([item.route]);
            if (!this.unespCoreMenuNavigationService.pinnedMenu) {
                this.unespCoreMenuNavigationService.closeNav();
            }
        }
        if (item.children && item.children.length) {
            this.expanded = !this.expanded;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavItemComponent, deps: [{ token: UnespCoreMenuNavigationService }, { token: i3.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.7", type: UnespCoreSidenavItemComponent, isStandalone: false, selector: "unesp-core-sidenav-item", inputs: { item: "item", depth: "depth" }, host: { properties: { "attr.aria-expanded": "this.ariaExpanded" } }, ngImport: i0, template: "<mat-divider *ngIf=\"item.addTopDivider && !item.hidden\"></mat-divider>\n\n<div fxLayout=\"row\" fxFlex=\"1 1 auto\" *ngIf=\"!item.hidden\"\n                    [ngStyle]=\"{'pointer-events': item.disabled ?'none':'auto', 'opacity': item.disabled? '0.15' : '1'}\"\n                    class=\"noselect\">\n<mat-list-item class=\"unesp-menu-mat-list-item\" fxFlex=\"1 1 auto\" fxLayout=\"row\" fxLayoutAlign=\"center stretch\"\n               [ngClass]=\"{ smallSize: item.smallSize }\">\n  <a (click)=\"onItemSelected(item)\"\n    [ngClass]=\"{ active: item.route ? router.isActive(item.route, true) : false, expanded: expanded}\"\n    fxLayout=\"row\" fxFlexFill fxLayoutAlign=\"space-between center\">\n    <div fxFlex=\"0 0 auto\" [ngStyle]=\"{ 'width': depth * 8 + 'px' }\"></div>\n\n    <mat-icon *ngIf=\"item.iconName\" class=\"unesp-menu-item-icon\" fxFlex=\"0 0 auto\">\n      {{item.iconName}}\n    </mat-icon>\n\n    <div class=\"unesp-menu-item\" [ngStyle]=\"(item.children && item.children.length>0)? { 'font-weight': '500' } : {}\" fxFlex=\"1 1 auto\">\n      {{ item.displayName }}\n    </div>\n\n    <div class=\"unesp-menu-item\" *ngIf=\"item.children && item.children.length && !item.disabled\" fxFlex=\"0 0 auto\">\n      <mat-icon [@indicatorRotate]=\"expanded ? 'expanded' : 'collapsed'\">\n        expand_more\n      </mat-icon>\n    </div>\n  </a>\n</mat-list-item>\n</div>\n\n<div *ngIf=\"expanded && !item.hidden && !item.disabled\" fxFlex=\"1 1 auto\" fxLayout=\"column\">\n  <unesp-core-sidenav-item *ngFor=\"let child of item.children\" [item]=\"child\" [depth]=\"depth+1\" fxFlex=\"0 0 auto\" fxLayout=\"column\">\n  </unesp-core-sidenav-item>\n</div>\n\n<mat-divider *ngIf=\"item.addBottomDivider && !item.hidden\" fxFlex=\"0 0 auto\"></mat-divider>\n", styles: [".unesp-menu-item-icon{margin-right:10px;color:#0000008a}.unesp-menu-item{font-size:16px;color:#000;white-space:normal}.mat-mdc-list-item:hover{background-color:#ddd}.unesp-menu-mat-list-item{padding:10px!important;height:45px!important}.unesp-menu-mat-list-item.smallSize{height:30px!important}.unesp-menu-mat-list-item-hidden{height:0}.noselect{-webkit-touch-callout:none;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}\n"], dependencies: [{ kind: "directive", type: i3$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i3$2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "component", type: i5$1.MatDivider, selector: "mat-divider", inputs: ["vertical", "inset"] }, { kind: "component", type: i5$2.MatListItem, selector: "mat-list-item, a[mat-list-item], button[mat-list-item]", inputs: ["activated"], exportAs: ["matListItem"] }, { kind: "directive", type: i1$2.DefaultLayoutDirective, selector: "  [fxLayout], [fxLayout.xs], [fxLayout.sm], [fxLayout.md],  [fxLayout.lg], [fxLayout.xl], [fxLayout.lt-sm], [fxLayout.lt-md],  [fxLayout.lt-lg], [fxLayout.lt-xl], [fxLayout.gt-xs], [fxLayout.gt-sm],  [fxLayout.gt-md], [fxLayout.gt-lg]", inputs: ["fxLayout", "fxLayout.xs", "fxLayout.sm", "fxLayout.md", "fxLayout.lg", "fxLayout.xl", "fxLayout.lt-sm", "fxLayout.lt-md", "fxLayout.lt-lg", "fxLayout.lt-xl", "fxLayout.gt-xs", "fxLayout.gt-sm", "fxLayout.gt-md", "fxLayout.gt-lg"] }, { kind: "directive", type: i1$2.DefaultLayoutAlignDirective, selector: "  [fxLayoutAlign], [fxLayoutAlign.xs], [fxLayoutAlign.sm], [fxLayoutAlign.md],  [fxLayoutAlign.lg], [fxLayoutAlign.xl], [fxLayoutAlign.lt-sm], [fxLayoutAlign.lt-md],  [fxLayoutAlign.lt-lg], [fxLayoutAlign.lt-xl], [fxLayoutAlign.gt-xs], [fxLayoutAlign.gt-sm],  [fxLayoutAlign.gt-md], [fxLayoutAlign.gt-lg]", inputs: ["fxLayoutAlign", "fxLayoutAlign.xs", "fxLayoutAlign.sm", "fxLayoutAlign.md", "fxLayoutAlign.lg", "fxLayoutAlign.xl", "fxLayoutAlign.lt-sm", "fxLayoutAlign.lt-md", "fxLayoutAlign.lt-lg", "fxLayoutAlign.lt-xl", "fxLayoutAlign.gt-xs", "fxLayoutAlign.gt-sm", "fxLayoutAlign.gt-md", "fxLayoutAlign.gt-lg"] }, { kind: "directive", type: i1$2.FlexFillDirective, selector: "[fxFill], [fxFlexFill]" }, { kind: "directive", type: i1$2.DefaultFlexDirective, selector: "  [fxFlex], [fxFlex.xs], [fxFlex.sm], [fxFlex.md],  [fxFlex.lg], [fxFlex.xl], [fxFlex.lt-sm], [fxFlex.lt-md],  [fxFlex.lt-lg], [fxFlex.lt-xl], [fxFlex.gt-xs], [fxFlex.gt-sm],  [fxFlex.gt-md], [fxFlex.gt-lg]", inputs: ["fxFlex", "fxFlex.xs", "fxFlex.sm", "fxFlex.md", "fxFlex.lg", "fxFlex.xl", "fxFlex.lt-sm", "fxFlex.lt-md", "fxFlex.lt-lg", "fxFlex.lt-xl", "fxFlex.gt-xs", "fxFlex.gt-sm", "fxFlex.gt-md", "fxFlex.gt-lg"] }, { kind: "directive", type: i8.DefaultClassDirective, selector: "  [ngClass], [ngClass.xs], [ngClass.sm], [ngClass.md], [ngClass.lg], [ngClass.xl],  [ngClass.lt-sm], [ngClass.lt-md], [ngClass.lt-lg], [ngClass.lt-xl],  [ngClass.gt-xs], [ngClass.gt-sm], [ngClass.gt-md], [ngClass.gt-lg]", inputs: ["ngClass", "ngClass.xs", "ngClass.sm", "ngClass.md", "ngClass.lg", "ngClass.xl", "ngClass.lt-sm", "ngClass.lt-md", "ngClass.lt-lg", "ngClass.lt-xl", "ngClass.gt-xs", "ngClass.gt-sm", "ngClass.gt-md", "ngClass.gt-lg"] }, { kind: "directive", type: i8.DefaultStyleDirective, selector: "  [ngStyle],  [ngStyle.xs], [ngStyle.sm], [ngStyle.md], [ngStyle.lg], [ngStyle.xl],  [ngStyle.lt-sm], [ngStyle.lt-md], [ngStyle.lt-lg], [ngStyle.lt-xl],  [ngStyle.gt-xs], [ngStyle.gt-sm], [ngStyle.gt-md], [ngStyle.gt-lg]", inputs: ["ngStyle", "ngStyle.xs", "ngStyle.sm", "ngStyle.md", "ngStyle.lg", "ngStyle.xl", "ngStyle.lt-sm", "ngStyle.lt-md", "ngStyle.lt-lg", "ngStyle.lt-xl", "ngStyle.gt-xs", "ngStyle.gt-sm", "ngStyle.gt-md", "ngStyle.gt-lg"] }, { kind: "component", type: UnespCoreSidenavItemComponent, selector: "unesp-core-sidenav-item", inputs: ["item", "depth"] }], animations: [
            trigger('indicatorRotate', [
                state('collapsed', style({ transform: 'rotate(0deg)' })),
                state('expanded', style({ transform: 'rotate(180deg)' })),
                transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4,0.0,0.2,1)')),
            ]),
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'unesp-core-sidenav-item', animations: [
                        trigger('indicatorRotate', [
                            state('collapsed', style({ transform: 'rotate(0deg)' })),
                            state('expanded', style({ transform: 'rotate(180deg)' })),
                            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4,0.0,0.2,1)')),
                        ]),
                    ], standalone: false, template: "<mat-divider *ngIf=\"item.addTopDivider && !item.hidden\"></mat-divider>\n\n<div fxLayout=\"row\" fxFlex=\"1 1 auto\" *ngIf=\"!item.hidden\"\n                    [ngStyle]=\"{'pointer-events': item.disabled ?'none':'auto', 'opacity': item.disabled? '0.15' : '1'}\"\n                    class=\"noselect\">\n<mat-list-item class=\"unesp-menu-mat-list-item\" fxFlex=\"1 1 auto\" fxLayout=\"row\" fxLayoutAlign=\"center stretch\"\n               [ngClass]=\"{ smallSize: item.smallSize }\">\n  <a (click)=\"onItemSelected(item)\"\n    [ngClass]=\"{ active: item.route ? router.isActive(item.route, true) : false, expanded: expanded}\"\n    fxLayout=\"row\" fxFlexFill fxLayoutAlign=\"space-between center\">\n    <div fxFlex=\"0 0 auto\" [ngStyle]=\"{ 'width': depth * 8 + 'px' }\"></div>\n\n    <mat-icon *ngIf=\"item.iconName\" class=\"unesp-menu-item-icon\" fxFlex=\"0 0 auto\">\n      {{item.iconName}}\n    </mat-icon>\n\n    <div class=\"unesp-menu-item\" [ngStyle]=\"(item.children && item.children.length>0)? { 'font-weight': '500' } : {}\" fxFlex=\"1 1 auto\">\n      {{ item.displayName }}\n    </div>\n\n    <div class=\"unesp-menu-item\" *ngIf=\"item.children && item.children.length && !item.disabled\" fxFlex=\"0 0 auto\">\n      <mat-icon [@indicatorRotate]=\"expanded ? 'expanded' : 'collapsed'\">\n        expand_more\n      </mat-icon>\n    </div>\n  </a>\n</mat-list-item>\n</div>\n\n<div *ngIf=\"expanded && !item.hidden && !item.disabled\" fxFlex=\"1 1 auto\" fxLayout=\"column\">\n  <unesp-core-sidenav-item *ngFor=\"let child of item.children\" [item]=\"child\" [depth]=\"depth+1\" fxFlex=\"0 0 auto\" fxLayout=\"column\">\n  </unesp-core-sidenav-item>\n</div>\n\n<mat-divider *ngIf=\"item.addBottomDivider && !item.hidden\" fxFlex=\"0 0 auto\"></mat-divider>\n", styles: [".unesp-menu-item-icon{margin-right:10px;color:#0000008a}.unesp-menu-item{font-size:16px;color:#000;white-space:normal}.mat-mdc-list-item:hover{background-color:#ddd}.unesp-menu-mat-list-item{padding:10px!important;height:45px!important}.unesp-menu-mat-list-item.smallSize{height:30px!important}.unesp-menu-mat-list-item-hidden{height:0}.noselect{-webkit-touch-callout:none;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}\n"] }]
        }], ctorParameters: () => [{ type: UnespCoreMenuNavigationService }, { type: i3.Router }], propDecorators: { ariaExpanded: [{
                type: HostBinding,
                args: ['attr.aria-expanded']
            }], item: [{
                type: Input
            }], depth: [{
                type: Input
            }] } });

/**
 * @description
 *
 * Este componente representa o menu da aplicação.
 *
 */
class UnespCoreSidenavComponent {
    constructor(unespCoreMenuNavigationService, unespCoreUserService, unespCoreAuthService, unespCoreLoaderService, cd, media) {
        this.unespCoreMenuNavigationService = unespCoreMenuNavigationService;
        this.unespCoreUserService = unespCoreUserService;
        this.unespCoreAuthService = unespCoreAuthService;
        this.unespCoreLoaderService = unespCoreLoaderService;
        this.cd = cd;
        this.media = media;
        /**
         * @description
         *
         * Exibe username (se existir) no topo do menu lateral.
         *
         */
        this.usernameOnSidenav = false;
        /**
         * @description
         *
         * Exibe botão de login/logout em baixo do menu lateral.
         *
         */
        this.loginOnSidenav = true;
        /**
         * @description
         *
         * Define se o menu lateral irá ser responsivo ao tamanho da tela.
         * Em resoluções maiores, ficará aberto e fixado ao lado.
         * Em resoluções menores, não ficará fixado.
         */
        this.fixedSidenavStyle = true;
        this.loading = false;
        this.itensMenu = [];
        this.lastMqAlias = '';
        this.media.asObservable().subscribe((change) => {
            if (change && change.length > 0 && this.fixedSidenavStyle) {
                this.lastMqAlias = change[0].mqAlias;
                this.setUnespDrawerStyle();
            }
        });
    }
    ngOnInit() {
        this.unespCoreMenuNavigationService.currentItensMenu.subscribe((itensMenu) => {
            this.itensMenu = itensMenu;
            if (this.itensMenu.length > 0) {
                this.setUnespDrawerStyle();
            }
        });
        this.unespCoreLoaderService.isLoading.pipe(delay(1)).subscribe((l) => {
            return (this.loading = l);
        });
    }
    ngAfterViewInit() {
        this.unespCoreMenuNavigationService.appDrawer = this.appDrawer;
        this.cd.detectChanges();
    }
    logout() {
        this.appDrawer.close();
        this.unespCoreAuthService.logout('Usuário desconectado com sucesso');
    }
    login() {
        window.location.href = this.unespCoreAuthService.getLoginUrl();
    }
    get user$() {
        return this.unespCoreUserService.user$;
    }
    setUnespDrawerStyle() {
        if (this.appDrawer) {
            if (!this.unespCoreUserService.getUser() && !this.unespCoreMenuNavigationService.publicMenu) {
                return;
            }
            if (this.lastMqAlias === 'lg' || this.lastMqAlias === 'xl') {
                this.appDrawer.open();
                this.appDrawer.disableClose = true;
                this.appDrawer.mode = 'side';
            }
            else {
                this.appDrawer.disableClose = false;
                this.appDrawer.mode = 'over';
                if (this.lastMqAlias === 'sm' || this.lastMqAlias === 'xs')
                    this.appDrawer.close();
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavComponent, deps: [{ token: UnespCoreMenuNavigationService }, { token: UnespCoreUserService }, { token: UnespCoreAuthService }, { token: UnespCoreLoaderService }, { token: i0.ChangeDetectorRef }, { token: i2$1.MediaObserver }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.7", type: UnespCoreSidenavComponent, isStandalone: false, selector: "unesp-core-sidenav", inputs: { usernameOnSidenav: "usernameOnSidenav", spinnerCustom: "spinnerCustom", loginOnSidenav: "loginOnSidenav", fixedSidenavStyle: "fixedSidenavStyle" }, viewQueries: [{ propertyName: "appDrawer", first: true, predicate: ["appDrawer"], descendants: true }], ngImport: i0, template: "<div *ngIf=\"loading\" class=\"loader-container\">\n  <div fxFlex=\"0 0 auto\" fxFlexFill fxLayoutAlign=\"center center\" class=\"spinner\">\n    <mat-progress-spinner *ngIf=\"!spinnerCustom\" mode=\"indeterminate\"></mat-progress-spinner>\n    <img *ngIf=\"spinnerCustom\" [src]='spinnerCustom'>\n  </div>\n</div>\n\n<mat-sidenav-container fxFlex=\"1 1 auto\">\n  <mat-sidenav #appDrawer fxLayout=\"column\" class=\"unesp-menu mat-elevation-z3\" fxFlex=\"1 1 auto\">\n    <mat-nav-list *ngIf=\"unespCoreMenuNavigationService.appDrawerEnabled\"\n                  fxFlex=\"1 1 auto\" fxLayout=\"column\" class=\"inline-list\">\n      <div *ngIf=\"usernameOnSidenav\" fxFlex=\"0 0 auto\">\n        <div *ngIf=\"user$ | async as user\" [ngClass]=\"'container-usuario'\">\n          <div [ngClass]=\"'container-usuario-title'\">Usu\u00E1rio</div>\n          <b [ngClass]=\"'container-usuario-username'\">\n            {{ user.identificacao }}\n          </b>\n        </div>\n        <mat-divider></mat-divider>\n      </div>\n\n      <unesp-core-sidenav-item *ngFor=\"let item of itensMenu\" [item]=\"item\" fxLayout=\"column\">\n      </unesp-core-sidenav-item>\n\n      <div *ngIf=\"loginOnSidenav\" fxFlex=\"0 0 auto\">\n        <mat-divider></mat-divider>\n\n        <a *ngIf=\"!(user$ | async)\" mat-list-item (click)=\"login()\">\n          <mat-icon class=\"unesp-menu-item-icon\">login</mat-icon>\n          <span class=\"unesp-menu-item\" fxFlex>Login</span>\n        </a>\n\n        <a *ngIf=\"user$ | async\" mat-list-item (click)=\"logout()\">\n          <mat-icon class=\"unesp-menu-item-icon\">login</mat-icon>\n          <span class=\"unesp-menu-item\" fxFlex>Sair</span>\n        </a>\n      </div>\n    </mat-nav-list>\n  </mat-sidenav>\n  <mat-sidenav-content fxFlex=\"1 1 auto\">\n    <ng-content select=\".ngcontent-content\"></ng-content>\n  </mat-sidenav-content>\n</mat-sidenav-container>\n", styles: [".unesp-menu{max-width:25vw}.unesp-menu-item-icon{margin-right:10px;color:#0000008a}.unesp-menu-item{font-size:16px;color:#000}.mat-mdc-list-item:hover{background-color:#ddd}.mdc-list-item{padding:10px}.mat-mdc-list-item{display:flex;flex-direction:row}.loader-container{position:absolute;height:100%;width:100%;top:0;left:0;z-index:9999}.container-usuario{height:45px;padding:20px;font-size:16px}.container-usuario-title{color:#0000008a;margin-bottom:8px}.container-usuario-username{color:#0000008a}.loader-container .spinner{position:fixed}.loader-container .spinner img{width:100px}\n"], dependencies: [{ kind: "directive", type: i3$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.MatSidenav, selector: "mat-sidenav", inputs: ["fixedInViewport", "fixedTopGap", "fixedBottomGap"], exportAs: ["matSidenav"] }, { kind: "component", type: i4.MatSidenavContainer, selector: "mat-sidenav-container", exportAs: ["matSidenavContainer"] }, { kind: "component", type: i4.MatSidenavContent, selector: "mat-sidenav-content" }, { kind: "component", type: i5$2.MatNavList, selector: "mat-nav-list", exportAs: ["matNavList"] }, { kind: "component", type: i5$2.MatListItem, selector: "mat-list-item, a[mat-list-item], button[mat-list-item]", inputs: ["activated"], exportAs: ["matListItem"] }, { kind: "component", type: i5$1.MatDivider, selector: "mat-divider", inputs: ["vertical", "inset"] }, { kind: "component", type: i3$2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "component", type: i8$1.MatProgressSpinner, selector: "mat-progress-spinner, mat-spinner", inputs: ["color", "mode", "value", "diameter", "strokeWidth"], exportAs: ["matProgressSpinner"] }, { kind: "component", type: UnespCoreSidenavItemComponent, selector: "unesp-core-sidenav-item", inputs: ["item", "depth"] }, { kind: "directive", type: i1$2.DefaultLayoutDirective, selector: "  [fxLayout], [fxLayout.xs], [fxLayout.sm], [fxLayout.md],  [fxLayout.lg], [fxLayout.xl], [fxLayout.lt-sm], [fxLayout.lt-md],  [fxLayout.lt-lg], [fxLayout.lt-xl], [fxLayout.gt-xs], [fxLayout.gt-sm],  [fxLayout.gt-md], [fxLayout.gt-lg]", inputs: ["fxLayout", "fxLayout.xs", "fxLayout.sm", "fxLayout.md", "fxLayout.lg", "fxLayout.xl", "fxLayout.lt-sm", "fxLayout.lt-md", "fxLayout.lt-lg", "fxLayout.lt-xl", "fxLayout.gt-xs", "fxLayout.gt-sm", "fxLayout.gt-md", "fxLayout.gt-lg"] }, { kind: "directive", type: i1$2.DefaultLayoutAlignDirective, selector: "  [fxLayoutAlign], [fxLayoutAlign.xs], [fxLayoutAlign.sm], [fxLayoutAlign.md],  [fxLayoutAlign.lg], [fxLayoutAlign.xl], [fxLayoutAlign.lt-sm], [fxLayoutAlign.lt-md],  [fxLayoutAlign.lt-lg], [fxLayoutAlign.lt-xl], [fxLayoutAlign.gt-xs], [fxLayoutAlign.gt-sm],  [fxLayoutAlign.gt-md], [fxLayoutAlign.gt-lg]", inputs: ["fxLayoutAlign", "fxLayoutAlign.xs", "fxLayoutAlign.sm", "fxLayoutAlign.md", "fxLayoutAlign.lg", "fxLayoutAlign.xl", "fxLayoutAlign.lt-sm", "fxLayoutAlign.lt-md", "fxLayoutAlign.lt-lg", "fxLayoutAlign.lt-xl", "fxLayoutAlign.gt-xs", "fxLayoutAlign.gt-sm", "fxLayoutAlign.gt-md", "fxLayoutAlign.gt-lg"] }, { kind: "directive", type: i1$2.FlexFillDirective, selector: "[fxFill], [fxFlexFill]" }, { kind: "directive", type: i1$2.DefaultFlexDirective, selector: "  [fxFlex], [fxFlex.xs], [fxFlex.sm], [fxFlex.md],  [fxFlex.lg], [fxFlex.xl], [fxFlex.lt-sm], [fxFlex.lt-md],  [fxFlex.lt-lg], [fxFlex.lt-xl], [fxFlex.gt-xs], [fxFlex.gt-sm],  [fxFlex.gt-md], [fxFlex.gt-lg]", inputs: ["fxFlex", "fxFlex.xs", "fxFlex.sm", "fxFlex.md", "fxFlex.lg", "fxFlex.xl", "fxFlex.lt-sm", "fxFlex.lt-md", "fxFlex.lt-lg", "fxFlex.lt-xl", "fxFlex.gt-xs", "fxFlex.gt-sm", "fxFlex.gt-md", "fxFlex.gt-lg"] }, { kind: "directive", type: i8.DefaultClassDirective, selector: "  [ngClass], [ngClass.xs], [ngClass.sm], [ngClass.md], [ngClass.lg], [ngClass.xl],  [ngClass.lt-sm], [ngClass.lt-md], [ngClass.lt-lg], [ngClass.lt-xl],  [ngClass.gt-xs], [ngClass.gt-sm], [ngClass.gt-md], [ngClass.gt-lg]", inputs: ["ngClass", "ngClass.xs", "ngClass.sm", "ngClass.md", "ngClass.lg", "ngClass.xl", "ngClass.lt-sm", "ngClass.lt-md", "ngClass.lt-lg", "ngClass.lt-xl", "ngClass.gt-xs", "ngClass.gt-sm", "ngClass.gt-md", "ngClass.gt-lg"] }, { kind: "pipe", type: i3$1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'unesp-core-sidenav', standalone: false, template: "<div *ngIf=\"loading\" class=\"loader-container\">\n  <div fxFlex=\"0 0 auto\" fxFlexFill fxLayoutAlign=\"center center\" class=\"spinner\">\n    <mat-progress-spinner *ngIf=\"!spinnerCustom\" mode=\"indeterminate\"></mat-progress-spinner>\n    <img *ngIf=\"spinnerCustom\" [src]='spinnerCustom'>\n  </div>\n</div>\n\n<mat-sidenav-container fxFlex=\"1 1 auto\">\n  <mat-sidenav #appDrawer fxLayout=\"column\" class=\"unesp-menu mat-elevation-z3\" fxFlex=\"1 1 auto\">\n    <mat-nav-list *ngIf=\"unespCoreMenuNavigationService.appDrawerEnabled\"\n                  fxFlex=\"1 1 auto\" fxLayout=\"column\" class=\"inline-list\">\n      <div *ngIf=\"usernameOnSidenav\" fxFlex=\"0 0 auto\">\n        <div *ngIf=\"user$ | async as user\" [ngClass]=\"'container-usuario'\">\n          <div [ngClass]=\"'container-usuario-title'\">Usu\u00E1rio</div>\n          <b [ngClass]=\"'container-usuario-username'\">\n            {{ user.identificacao }}\n          </b>\n        </div>\n        <mat-divider></mat-divider>\n      </div>\n\n      <unesp-core-sidenav-item *ngFor=\"let item of itensMenu\" [item]=\"item\" fxLayout=\"column\">\n      </unesp-core-sidenav-item>\n\n      <div *ngIf=\"loginOnSidenav\" fxFlex=\"0 0 auto\">\n        <mat-divider></mat-divider>\n\n        <a *ngIf=\"!(user$ | async)\" mat-list-item (click)=\"login()\">\n          <mat-icon class=\"unesp-menu-item-icon\">login</mat-icon>\n          <span class=\"unesp-menu-item\" fxFlex>Login</span>\n        </a>\n\n        <a *ngIf=\"user$ | async\" mat-list-item (click)=\"logout()\">\n          <mat-icon class=\"unesp-menu-item-icon\">login</mat-icon>\n          <span class=\"unesp-menu-item\" fxFlex>Sair</span>\n        </a>\n      </div>\n    </mat-nav-list>\n  </mat-sidenav>\n  <mat-sidenav-content fxFlex=\"1 1 auto\">\n    <ng-content select=\".ngcontent-content\"></ng-content>\n  </mat-sidenav-content>\n</mat-sidenav-container>\n", styles: [".unesp-menu{max-width:25vw}.unesp-menu-item-icon{margin-right:10px;color:#0000008a}.unesp-menu-item{font-size:16px;color:#000}.mat-mdc-list-item:hover{background-color:#ddd}.mdc-list-item{padding:10px}.mat-mdc-list-item{display:flex;flex-direction:row}.loader-container{position:absolute;height:100%;width:100%;top:0;left:0;z-index:9999}.container-usuario{height:45px;padding:20px;font-size:16px}.container-usuario-title{color:#0000008a;margin-bottom:8px}.container-usuario-username{color:#0000008a}.loader-container .spinner{position:fixed}.loader-container .spinner img{width:100px}\n"] }]
        }], ctorParameters: () => [{ type: UnespCoreMenuNavigationService }, { type: UnespCoreUserService }, { type: UnespCoreAuthService }, { type: UnespCoreLoaderService }, { type: i0.ChangeDetectorRef }, { type: i2$1.MediaObserver }], propDecorators: { appDrawer: [{
                type: ViewChild,
                args: ['appDrawer']
            }], usernameOnSidenav: [{
                type: Input
            }], spinnerCustom: [{
                type: Input
            }], loginOnSidenav: [{
                type: Input
            }], fixedSidenavStyle: [{
                type: Input
            }] } });

/**
 * @description
 *
 * Este componente representa o rodapé da aplicação.
 * O desenvolvedor pode injetar conteúdo HTML no template por meio do seletor de ng-content chamado 'ngcontent-select-footer'
 *
 */
class UnespCoreFooterComponent {
    constructor() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.7", type: UnespCoreFooterComponent, isStandalone: false, selector: "unesp-core-footer", ngImport: i0, template: "<footer fxFlex>\n  <ng-content select=\".ngcontent-select-footer\"></ng-content>\n</footer>\n", styles: ["footer{color:#888;background-color:#e2e2e2;border-top:1px solid #ddd;padding:10px;text-align:center}\n"], dependencies: [{ kind: "directive", type: i1$2.DefaultFlexDirective, selector: "  [fxFlex], [fxFlex.xs], [fxFlex.sm], [fxFlex.md],  [fxFlex.lg], [fxFlex.xl], [fxFlex.lt-sm], [fxFlex.lt-md],  [fxFlex.lt-lg], [fxFlex.lt-xl], [fxFlex.gt-xs], [fxFlex.gt-sm],  [fxFlex.gt-md], [fxFlex.gt-lg]", inputs: ["fxFlex", "fxFlex.xs", "fxFlex.sm", "fxFlex.md", "fxFlex.lg", "fxFlex.xl", "fxFlex.lt-sm", "fxFlex.lt-md", "fxFlex.lt-lg", "fxFlex.lt-xl", "fxFlex.gt-xs", "fxFlex.gt-sm", "fxFlex.gt-md", "fxFlex.gt-lg"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreFooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'unesp-core-footer', standalone: false, template: "<footer fxFlex>\n  <ng-content select=\".ngcontent-select-footer\"></ng-content>\n</footer>\n", styles: ["footer{color:#888;background-color:#e2e2e2;border-top:1px solid #ddd;padding:10px;text-align:center}\n"] }]
        }], ctorParameters: () => [] });

/**
 * @description
 *
 * Este componente agrupa os componentes de layout da aplicação em um formato fixo e padronizado.
 *
 */
class UnespCoreLayoutComponent {
    constructor() {
        /**
         * @description
         *
         * Define se exibirá o botão de fixar o menu no header
         */
        this.showPinMenu = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLayoutComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.7", type: UnespCoreLayoutComponent, isStandalone: false, selector: "unesp-core-layout", inputs: { fullTitle: "fullTitle", shortTitle: "shortTitle", footer: "footer", logoCustom: "logoCustom", spinnerCustom: "spinnerCustom", showPinMenu: "showPinMenu" }, ngImport: i0, template: "<div fxLayout=\"column\" fxLayoutAlign=\"space-between stretch\" fxFlexFill>\n  <div fxFlex=\"0 0 auto\" fxLayout=\"row\" fxLayoutAlign=\"space-between\">\n    <unesp-core-header [showPinMenu]=\"showPinMenu\" fullTitle=\"{{fullTitle}}\" shortTitle=\"{{shortTitle}}\" logoCustom=\"{{logoCustom}}\" fxFlexFill></unesp-core-header>\n  </div>\n  <div fxFlex=\"1 1 auto\">\n    <unesp-core-sidenav fxFlex=\"1 1 auto\" spinnerCustom=\"{{spinnerCustom}}\">\n      <div class=\"ngcontent-content\" fxFlexFill>\n        <ng-content select=\".ngcontent-router\"></ng-content>\n      </div>\n    </unesp-core-sidenav>\n  </div>\n  <div fxFlex=\"0 0 auto\" fxLayout=\"row\" id=\"unesp-core-footer\">\n    <unesp-core-footer fxFlexFill>\n      <div class=\"ngcontent-select-footer\">\n        <address>\n          \u00C1rea respons\u00E1vel: <a target=\"_blank\" [href]=\"footer.areaResponsavelUrl\" class=\"footer-url\"> {{footer.areaResponsavel}}</a>\n          <br>\n          Propriet\u00E1rio: <a target=\"_blank\" [href]=\"footer.proprietarioUrl\" class=\"footer-url\"> {{footer.proprietario}}</a>\n          | Desenvolvido por: <a target=\"_blank\" [href]=\"footer.desenvolvidoPorUrl\" class=\"footer-url\"> {{footer.desenvolvidoPor}}</a>\n        </address>\n      </div>\n    </unesp-core-footer>\n  </div>\n</div>\n", styles: [".footer-url{text-decoration:none;color:#888;font-weight:700;font-size:small}\n"], dependencies: [{ kind: "directive", type: i1$2.DefaultLayoutDirective, selector: "  [fxLayout], [fxLayout.xs], [fxLayout.sm], [fxLayout.md],  [fxLayout.lg], [fxLayout.xl], [fxLayout.lt-sm], [fxLayout.lt-md],  [fxLayout.lt-lg], [fxLayout.lt-xl], [fxLayout.gt-xs], [fxLayout.gt-sm],  [fxLayout.gt-md], [fxLayout.gt-lg]", inputs: ["fxLayout", "fxLayout.xs", "fxLayout.sm", "fxLayout.md", "fxLayout.lg", "fxLayout.xl", "fxLayout.lt-sm", "fxLayout.lt-md", "fxLayout.lt-lg", "fxLayout.lt-xl", "fxLayout.gt-xs", "fxLayout.gt-sm", "fxLayout.gt-md", "fxLayout.gt-lg"] }, { kind: "directive", type: i1$2.DefaultLayoutAlignDirective, selector: "  [fxLayoutAlign], [fxLayoutAlign.xs], [fxLayoutAlign.sm], [fxLayoutAlign.md],  [fxLayoutAlign.lg], [fxLayoutAlign.xl], [fxLayoutAlign.lt-sm], [fxLayoutAlign.lt-md],  [fxLayoutAlign.lt-lg], [fxLayoutAlign.lt-xl], [fxLayoutAlign.gt-xs], [fxLayoutAlign.gt-sm],  [fxLayoutAlign.gt-md], [fxLayoutAlign.gt-lg]", inputs: ["fxLayoutAlign", "fxLayoutAlign.xs", "fxLayoutAlign.sm", "fxLayoutAlign.md", "fxLayoutAlign.lg", "fxLayoutAlign.xl", "fxLayoutAlign.lt-sm", "fxLayoutAlign.lt-md", "fxLayoutAlign.lt-lg", "fxLayoutAlign.lt-xl", "fxLayoutAlign.gt-xs", "fxLayoutAlign.gt-sm", "fxLayoutAlign.gt-md", "fxLayoutAlign.gt-lg"] }, { kind: "directive", type: i1$2.FlexFillDirective, selector: "[fxFill], [fxFlexFill]" }, { kind: "directive", type: i1$2.DefaultFlexDirective, selector: "  [fxFlex], [fxFlex.xs], [fxFlex.sm], [fxFlex.md],  [fxFlex.lg], [fxFlex.xl], [fxFlex.lt-sm], [fxFlex.lt-md],  [fxFlex.lt-lg], [fxFlex.lt-xl], [fxFlex.gt-xs], [fxFlex.gt-sm],  [fxFlex.gt-md], [fxFlex.gt-lg]", inputs: ["fxFlex", "fxFlex.xs", "fxFlex.sm", "fxFlex.md", "fxFlex.lg", "fxFlex.xl", "fxFlex.lt-sm", "fxFlex.lt-md", "fxFlex.lt-lg", "fxFlex.lt-xl", "fxFlex.gt-xs", "fxFlex.gt-sm", "fxFlex.gt-md", "fxFlex.gt-lg"] }, { kind: "component", type: UnespCoreHeaderComponent, selector: "unesp-core-header", inputs: ["fullTitle", "shortTitle", "usernameOnHeader", "logo", "logoCustom", "publicMenu", "showCountdownTimer", "loginOnHeader", "logoutOnHeader", "showPinMenu"] }, { kind: "component", type: UnespCoreSidenavComponent, selector: "unesp-core-sidenav", inputs: ["usernameOnSidenav", "spinnerCustom", "loginOnSidenav", "fixedSidenavStyle"] }, { kind: "component", type: UnespCoreFooterComponent, selector: "unesp-core-footer" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'unesp-core-layout', standalone: false, template: "<div fxLayout=\"column\" fxLayoutAlign=\"space-between stretch\" fxFlexFill>\n  <div fxFlex=\"0 0 auto\" fxLayout=\"row\" fxLayoutAlign=\"space-between\">\n    <unesp-core-header [showPinMenu]=\"showPinMenu\" fullTitle=\"{{fullTitle}}\" shortTitle=\"{{shortTitle}}\" logoCustom=\"{{logoCustom}}\" fxFlexFill></unesp-core-header>\n  </div>\n  <div fxFlex=\"1 1 auto\">\n    <unesp-core-sidenav fxFlex=\"1 1 auto\" spinnerCustom=\"{{spinnerCustom}}\">\n      <div class=\"ngcontent-content\" fxFlexFill>\n        <ng-content select=\".ngcontent-router\"></ng-content>\n      </div>\n    </unesp-core-sidenav>\n  </div>\n  <div fxFlex=\"0 0 auto\" fxLayout=\"row\" id=\"unesp-core-footer\">\n    <unesp-core-footer fxFlexFill>\n      <div class=\"ngcontent-select-footer\">\n        <address>\n          \u00C1rea respons\u00E1vel: <a target=\"_blank\" [href]=\"footer.areaResponsavelUrl\" class=\"footer-url\"> {{footer.areaResponsavel}}</a>\n          <br>\n          Propriet\u00E1rio: <a target=\"_blank\" [href]=\"footer.proprietarioUrl\" class=\"footer-url\"> {{footer.proprietario}}</a>\n          | Desenvolvido por: <a target=\"_blank\" [href]=\"footer.desenvolvidoPorUrl\" class=\"footer-url\"> {{footer.desenvolvidoPor}}</a>\n        </address>\n      </div>\n    </unesp-core-footer>\n  </div>\n</div>\n", styles: [".footer-url{text-decoration:none;color:#888;font-weight:700;font-size:small}\n"] }]
        }], propDecorators: { fullTitle: [{
                type: Input
            }], shortTitle: [{
                type: Input
            }], footer: [{
                type: Input
            }], logoCustom: [{
                type: Input
            }], spinnerCustom: [{
                type: Input
            }], showPinMenu: [{
                type: Input
            }] } });

class UnespCoreFooterModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreFooterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreFooterModule, declarations: [UnespCoreFooterComponent], imports: [FlexLayoutModule], exports: [UnespCoreFooterComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreFooterModule, imports: [FlexLayoutModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreFooterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        FlexLayoutModule
                    ],
                    declarations: [
                        UnespCoreFooterComponent
                    ],
                    exports: [
                        UnespCoreFooterComponent
                    ]
                }]
        }] });

class UnespCoreSidenavItemModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavItemModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavItemModule, declarations: [UnespCoreSidenavItemComponent], imports: [CommonModule,
            MatIconModule,
            MatDividerModule,
            MatListModule,
            FlexLayoutModule], exports: [UnespCoreSidenavItemComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavItemModule, imports: [CommonModule,
            MatIconModule,
            MatDividerModule,
            MatListModule,
            FlexLayoutModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavItemModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatIconModule,
                        MatDividerModule,
                        MatListModule,
                        FlexLayoutModule
                    ],
                    declarations: [
                        UnespCoreSidenavItemComponent
                    ],
                    exports: [
                        UnespCoreSidenavItemComponent
                    ]
                }]
        }] });

class UnespCoreSidenavModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavModule, declarations: [UnespCoreSidenavComponent], imports: [CommonModule,
            MatSidenavModule,
            MatListModule,
            MatIconModule,
            MatProgressSpinnerModule,
            UnespCoreSidenavItemModule,
            FlexLayoutModule], exports: [UnespCoreSidenavComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavModule, imports: [CommonModule,
            MatSidenavModule,
            MatListModule,
            MatIconModule,
            MatProgressSpinnerModule,
            UnespCoreSidenavItemModule,
            FlexLayoutModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreSidenavModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatSidenavModule,
                        MatListModule,
                        MatIconModule,
                        MatProgressSpinnerModule,
                        UnespCoreSidenavItemModule,
                        FlexLayoutModule
                    ],
                    declarations: [
                        UnespCoreSidenavComponent
                    ],
                    exports: [
                        UnespCoreSidenavComponent
                    ]
                }]
        }] });

class UnespCoreHeaderCountdownModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderCountdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderCountdownModule, declarations: [UnespCoreHeaderCountdownComponent], imports: [CommonModule,
            CountdownModule,
            MatButtonModule,
            FlexModule], exports: [UnespCoreHeaderCountdownComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderCountdownModule, imports: [CommonModule,
            CountdownModule,
            MatButtonModule,
            FlexModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderCountdownModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        UnespCoreHeaderCountdownComponent
                    ],
                    exports: [
                        UnespCoreHeaderCountdownComponent
                    ],
                    imports: [
                        CommonModule,
                        CountdownModule,
                        MatButtonModule,
                        FlexModule
                    ]
                }]
        }] });

class UnespCoreHeaderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderModule, declarations: [UnespCoreHeaderComponent], imports: [CommonModule,
            MatToolbarModule,
            MatIconModule,
            MatButtonModule,
            FlexLayoutModule,
            UnespCoreHeaderCountdownModule], exports: [UnespCoreHeaderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderModule, imports: [CommonModule,
            MatToolbarModule,
            MatIconModule,
            MatButtonModule,
            FlexLayoutModule,
            UnespCoreHeaderCountdownModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatToolbarModule,
                        MatIconModule,
                        MatButtonModule,
                        FlexLayoutModule,
                        UnespCoreHeaderCountdownModule
                    ],
                    declarations: [
                        UnespCoreHeaderComponent
                    ],
                    exports: [
                        UnespCoreHeaderComponent
                    ]
                }]
        }] });

class UnespCoreLayoutModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLayoutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLayoutModule, declarations: [UnespCoreLayoutComponent], imports: [FlexLayoutModule,
            UnespCoreHeaderModule,
            UnespCoreSidenavModule,
            UnespCoreFooterModule], exports: [UnespCoreLayoutComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLayoutModule, imports: [FlexLayoutModule,
            UnespCoreHeaderModule,
            UnespCoreSidenavModule,
            UnespCoreFooterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLayoutModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        FlexLayoutModule,
                        UnespCoreHeaderModule,
                        UnespCoreSidenavModule,
                        UnespCoreFooterModule
                    ],
                    declarations: [
                        UnespCoreLayoutComponent
                    ],
                    exports: [
                        UnespCoreLayoutComponent
                    ]
                }]
        }] });

/**
 * @description
 *
 * Este componente representa uma página utilizada quando um usuário tenta acessar uma rota sem permissão.
 * Sua injeção é acionada automaticamente quando o UnespCoreAuthGuardService verifica o acesso indevido.
 *
 * Lembre-se de acrescentar ao 'app-routing.module' a seguinte rota:
 *
 * ```
 * ...
 *
 * {
 *   path: 'access-denied',
 *   component: UnespCoreAccessDeniedComponent
 * },
 * ...
 * ```
 */
class UnespCoreAccessDeniedComponent {
    constructor(router) {
        this.router = router;
    }
    voltar() {
        this.router.navigate(['/']);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAccessDeniedComponent, deps: [{ token: i3.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.7", type: UnespCoreAccessDeniedComponent, isStandalone: false, selector: "unesp-core-accessdenied", ngImport: i0, template: "<form novalidate>\n    <mat-card style=\"width: 100%; text-align: center;\">\n        <mat-card-header style=\"flex-direction: column;\">\n            <mat-card-title >Acesso negado!</mat-card-title>\n        </mat-card-header>\n\n        <mat-card-content>\n            <div>\n                <button type=\"button\" mat-raised-button (click)=\"voltar()\" color=\"primary\">\n                    <mat-icon>undo</mat-icon> Voltar\n                </button>\n            </div>\n        </mat-card-content>\n    </mat-card>\n</form>\n", styles: [""], dependencies: [{ kind: "component", type: i2$2.MatCard, selector: "mat-card", inputs: ["appearance"], exportAs: ["matCard"] }, { kind: "directive", type: i2$2.MatCardContent, selector: "mat-card-content" }, { kind: "component", type: i2$2.MatCardHeader, selector: "mat-card-header" }, { kind: "directive", type: i2$2.MatCardTitle, selector: "mat-card-title, [mat-card-title], [matCardTitle]" }, { kind: "component", type: i3$2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "component", type: i7.MatButton, selector: "    button[mat-button], button[mat-raised-button], button[mat-flat-button],    button[mat-stroked-button]  ", exportAs: ["matButton"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAccessDeniedComponent, decorators: [{
            type: Component,
            args: [{ selector: 'unesp-core-accessdenied', standalone: false, template: "<form novalidate>\n    <mat-card style=\"width: 100%; text-align: center;\">\n        <mat-card-header style=\"flex-direction: column;\">\n            <mat-card-title >Acesso negado!</mat-card-title>\n        </mat-card-header>\n\n        <mat-card-content>\n            <div>\n                <button type=\"button\" mat-raised-button (click)=\"voltar()\" color=\"primary\">\n                    <mat-icon>undo</mat-icon> Voltar\n                </button>\n            </div>\n        </mat-card-content>\n    </mat-card>\n</form>\n" }]
        }], ctorParameters: () => [{ type: i3.Router }] });

class UnespCoreAccessDeniedModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAccessDeniedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAccessDeniedModule, declarations: [UnespCoreAccessDeniedComponent], imports: [MatCardModule,
            MatIconModule,
            MatButtonModule], exports: [UnespCoreAccessDeniedComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAccessDeniedModule, imports: [MatCardModule,
            MatIconModule,
            MatButtonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAccessDeniedModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        MatCardModule,
                        MatIconModule,
                        MatButtonModule
                    ],
                    declarations: [
                        UnespCoreAccessDeniedComponent
                    ],
                    exports: [
                        UnespCoreAccessDeniedComponent
                    ]
                }]
        }] });

/**
 * @description
 *
 * Este componente representa a página home da aplicação.
 *
 */
class UnespCoreHomeComponent {
    constructor(unespCoreAuthService, unespCoreUserService, activatedRoute, router, unespCoreMenuNavigationService) {
        this.unespCoreAuthService = unespCoreAuthService;
        this.unespCoreUserService = unespCoreUserService;
        this.activatedRoute = activatedRoute;
        this.router = router;
        this.unespCoreMenuNavigationService = unespCoreMenuNavigationService;
        this.nomeUsuario = '';
    }
    ngOnInit() {
        this.activatedRoute.queryParams.subscribe((params) => {
            const authorizationCode = params.code;
            if (authorizationCode) {
                this.unespCoreAuthService.retrieveToken(authorizationCode);
                this.router.navigate(['']);
            }
            else {
                const tryAuth = params.tryAuth;
                if (tryAuth) {
                    if (!this.unespCoreUserService.getUser()) {
                        this.login();
                    }
                    else {
                        this.router.navigate(['']);
                    }
                }
            }
        });
        this.userSubscription = this.unespCoreUserService.user$.subscribe(usuario => {
            this.nomeUsuario = usuario ? ', ' + usuario.nomeUsuario + '!' : '.';
        });
    }
    ngOnDestroy() {
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }
    }
    get user$() {
        return this.unespCoreUserService.user$;
    }
    login() {
        window.location.href = this.unespCoreAuthService.getLoginUrl();
    }
    logout() {
        this.unespCoreAuthService.logout('Usuário desconectado com sucesso');
        this.unespCoreMenuNavigationService.appDrawer?.close();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHomeComponent, deps: [{ token: UnespCoreAuthService }, { token: UnespCoreUserService }, { token: i3.ActivatedRoute }, { token: i3.Router }, { token: UnespCoreMenuNavigationService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.7", type: UnespCoreHomeComponent, isStandalone: false, selector: "unesp-core-home", ngImport: i0, template: "<div *ngIf=\"!(user$ | async)\" class=\"main-div\">\n  <mat-card class=\"card\">\n    <mat-card-header>\n      <mat-card-title>Bem vindo(a)</mat-card-title>\n      <mat-card-subtitle class=\"card-subtitle\">Efetue login para acessar o sistema</mat-card-subtitle>\n    </mat-card-header>\n    <mat-card-actions class=\"card-button\">\n      <button mat-raised-button color=\"primary\" (click)=\"login()\">Entrar usando Central de Acessos</button>\n    </mat-card-actions>\n  </mat-card>\n</div>\n\n<div *ngIf=\"user$ | async\" class=\"main-div\">\n  <mat-card class=\"card\">\n    <mat-card-header>\n      <mat-card-title>Ol\u00E1{{nomeUsuario}}</mat-card-title>\n      <mat-card-subtitle class=\"card-subtitle\">Utilize o menu para acessar as funcionalidades</mat-card-subtitle>\n    </mat-card-header>\n  </mat-card>\n</div>\n", styles: [".main-div{display:flex;justify-content:center;align-items:center}.card{margin-top:20px;max-width:400px;width:80%;height:10rem;display:flex;justify-content:space-around}.mat-mdc-raised-button{letter-spacing:normal!important}.card-button{justify-content:center}.card-subtitle{padding-top:10px}\n"], dependencies: [{ kind: "directive", type: i3$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$2.MatCard, selector: "mat-card", inputs: ["appearance"], exportAs: ["matCard"] }, { kind: "directive", type: i2$2.MatCardActions, selector: "mat-card-actions", inputs: ["align"], exportAs: ["matCardActions"] }, { kind: "component", type: i2$2.MatCardHeader, selector: "mat-card-header" }, { kind: "directive", type: i2$2.MatCardSubtitle, selector: "mat-card-subtitle, [mat-card-subtitle], [matCardSubtitle]" }, { kind: "directive", type: i2$2.MatCardTitle, selector: "mat-card-title, [mat-card-title], [matCardTitle]" }, { kind: "component", type: i7.MatButton, selector: "    button[mat-button], button[mat-raised-button], button[mat-flat-button],    button[mat-stroked-button]  ", exportAs: ["matButton"] }, { kind: "pipe", type: i3$1.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHomeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'unesp-core-home', standalone: false, template: "<div *ngIf=\"!(user$ | async)\" class=\"main-div\">\n  <mat-card class=\"card\">\n    <mat-card-header>\n      <mat-card-title>Bem vindo(a)</mat-card-title>\n      <mat-card-subtitle class=\"card-subtitle\">Efetue login para acessar o sistema</mat-card-subtitle>\n    </mat-card-header>\n    <mat-card-actions class=\"card-button\">\n      <button mat-raised-button color=\"primary\" (click)=\"login()\">Entrar usando Central de Acessos</button>\n    </mat-card-actions>\n  </mat-card>\n</div>\n\n<div *ngIf=\"user$ | async\" class=\"main-div\">\n  <mat-card class=\"card\">\n    <mat-card-header>\n      <mat-card-title>Ol\u00E1{{nomeUsuario}}</mat-card-title>\n      <mat-card-subtitle class=\"card-subtitle\">Utilize o menu para acessar as funcionalidades</mat-card-subtitle>\n    </mat-card-header>\n  </mat-card>\n</div>\n", styles: [".main-div{display:flex;justify-content:center;align-items:center}.card{margin-top:20px;max-width:400px;width:80%;height:10rem;display:flex;justify-content:space-around}.mat-mdc-raised-button{letter-spacing:normal!important}.card-button{justify-content:center}.card-subtitle{padding-top:10px}\n"] }]
        }], ctorParameters: () => [{ type: UnespCoreAuthService }, { type: UnespCoreUserService }, { type: i3.ActivatedRoute }, { type: i3.Router }, { type: UnespCoreMenuNavigationService }] });

class UnespCoreHomeModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHomeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHomeModule, declarations: [UnespCoreHomeComponent], imports: [CommonModule,
            MatCardModule,
            MatButtonModule], exports: [UnespCoreHomeComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHomeModule, imports: [CommonModule,
            MatCardModule,
            MatButtonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreHomeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatCardModule,
                        MatButtonModule
                    ],
                    declarations: [
                        UnespCoreHomeComponent
                    ],
                    exports: [
                        UnespCoreHomeComponent
                    ]
                }]
        }] });

/**
 * @description
 *
 * Este componente representa uma página utilizada quando um usuário tenta acessar uma página não existente (404).
  *
 * Lembre-se de acrescentar ao FINAL DE SEU 'app-routing.module' as seguintes rotas:
 *
 * ```
 * ...
 *
*  {
*    path: 'page-not-found',
*    component: UnespCorePageNotFoundComponent
*  },
*  {
*    path: '**',
*    redirectTo: '/page-not-found'
*  },
 * ...
 * ```
 */
class UnespCorePageNotFoundComponent {
    constructor(router) {
        this.router = router;
    }
    voltar() {
        this.router.navigate(['/']);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCorePageNotFoundComponent, deps: [{ token: i3.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.1.7", type: UnespCorePageNotFoundComponent, isStandalone: false, selector: "unesp-core-pagenotfound", ngImport: i0, template: "<form novalidate>\n    <mat-card class=\"page-not-found\">\n        <mat-card-header style=\"flex-direction: column;\">\n            <mat-card-title >P\u00E1gina n\u00E3o encontrada!</mat-card-title>\n        </mat-card-header>\n\n        <mat-card-content>\n            <div>\n                <img src=\"assets/img/page-not-found.png\">\n            </div>\n            \n            <div>\n                <button type=\"button\" mat-raised-button (click)=\"voltar()\" color=\"primary\">\n                    <mat-icon>undo</mat-icon> Voltar\n                </button>\n            </div>\n        </mat-card-content>\n    </mat-card>    \n</form>\n", styles: [".page-not-found{width:100%;text-align:center;margin-top:10px}\n"], dependencies: [{ kind: "component", type: i2$2.MatCard, selector: "mat-card", inputs: ["appearance"], exportAs: ["matCard"] }, { kind: "directive", type: i2$2.MatCardContent, selector: "mat-card-content" }, { kind: "component", type: i2$2.MatCardHeader, selector: "mat-card-header" }, { kind: "directive", type: i2$2.MatCardTitle, selector: "mat-card-title, [mat-card-title], [matCardTitle]" }, { kind: "component", type: i3$2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "component", type: i7.MatButton, selector: "    button[mat-button], button[mat-raised-button], button[mat-flat-button],    button[mat-stroked-button]  ", exportAs: ["matButton"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCorePageNotFoundComponent, decorators: [{
            type: Component,
            args: [{ selector: 'unesp-core-pagenotfound', standalone: false, template: "<form novalidate>\n    <mat-card class=\"page-not-found\">\n        <mat-card-header style=\"flex-direction: column;\">\n            <mat-card-title >P\u00E1gina n\u00E3o encontrada!</mat-card-title>\n        </mat-card-header>\n\n        <mat-card-content>\n            <div>\n                <img src=\"assets/img/page-not-found.png\">\n            </div>\n            \n            <div>\n                <button type=\"button\" mat-raised-button (click)=\"voltar()\" color=\"primary\">\n                    <mat-icon>undo</mat-icon> Voltar\n                </button>\n            </div>\n        </mat-card-content>\n    </mat-card>    \n</form>\n", styles: [".page-not-found{width:100%;text-align:center;margin-top:10px}\n"] }]
        }], ctorParameters: () => [{ type: i3.Router }] });

class UnespCorePageNotFoundModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCorePageNotFoundModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCorePageNotFoundModule, declarations: [UnespCorePageNotFoundComponent], imports: [MatCardModule,
            MatIconModule,
            MatButtonModule], exports: [UnespCorePageNotFoundComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCorePageNotFoundModule, imports: [MatCardModule,
            MatIconModule,
            MatButtonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCorePageNotFoundModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        MatCardModule,
                        MatIconModule,
                        MatButtonModule
                    ],
                    declarations: [
                        UnespCorePageNotFoundComponent
                    ],
                    exports: [
                        UnespCorePageNotFoundComponent
                    ]
                }]
        }] });

class UnespCoreComponentsModule {
    constructor(matIconRegistry, domSanitizer) {
        this.matIconRegistry = matIconRegistry;
        this.domSanitizer = domSanitizer;
        // Registra ícones customizados
        this.registerIcons();
    }
    registerIcons() {
        const keepSVG = `
    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e8eaed">
    <path d="m640-480 80 80v80H520v240l-40
    40-40-40v-240H240v-80l80-80v-280h-40v-80h400v80h-40v280Zm-286 80h252l-46-46v-314H400v314l-46 46Zm126 0Z"/></svg>
    `;
        this.matIconRegistry.addSvgIconLiteral('keep', this.domSanitizer.bypassSecurityTrustHtml(keepSVG));
        const keepOffSVG = `
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e8eaed">
      <path d="M680-840v80h-40v327l-80-80v-247H400v87l-87-87-33-33v-47h400ZM480-40l-40-40v-240H240v-80l80-80v-46L56-792l56-56
      736 736-58 56-264-264h-6v240l-40 40ZM354-400h92l-44-44-2-2-46 46Zm126-193Zm-78 149Z"/></svg>`;
        this.matIconRegistry.addSvgIconLiteral('keep_off', this.domSanitizer.bypassSecurityTrustHtml(keepOffSVG));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreComponentsModule, deps: [{ token: i3$2.MatIconRegistry }, { token: i2$3.DomSanitizer }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreComponentsModule, imports: [UnespCoreAccessDeniedModule,
            UnespCoreFooterModule,
            UnespCoreHeaderModule,
            UnespCoreHeaderCountdownModule,
            UnespCoreHomeModule,
            UnespCorePageNotFoundModule,
            UnespCoreSidenavModule,
            UnespCoreSidenavItemModule,
            UnespCoreLayoutModule], exports: [UnespCoreAccessDeniedModule,
            UnespCoreFooterModule,
            UnespCoreHeaderModule,
            UnespCoreHeaderCountdownModule,
            UnespCoreHomeModule,
            UnespCorePageNotFoundModule,
            UnespCoreSidenavModule,
            UnespCoreSidenavItemModule,
            UnespCoreLayoutModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreComponentsModule, imports: [UnespCoreAccessDeniedModule,
            UnespCoreFooterModule,
            UnespCoreHeaderModule,
            UnespCoreHeaderCountdownModule,
            UnespCoreHomeModule,
            UnespCorePageNotFoundModule,
            UnespCoreSidenavModule,
            UnespCoreSidenavItemModule,
            UnespCoreLayoutModule, UnespCoreAccessDeniedModule,
            UnespCoreFooterModule,
            UnespCoreHeaderModule,
            UnespCoreHeaderCountdownModule,
            UnespCoreHomeModule,
            UnespCorePageNotFoundModule,
            UnespCoreSidenavModule,
            UnespCoreSidenavItemModule,
            UnespCoreLayoutModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreComponentsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        UnespCoreAccessDeniedModule,
                        UnespCoreFooterModule,
                        UnespCoreHeaderModule,
                        UnespCoreHeaderCountdownModule,
                        UnespCoreHomeModule,
                        UnespCorePageNotFoundModule,
                        UnespCoreSidenavModule,
                        UnespCoreSidenavItemModule,
                        UnespCoreLayoutModule
                    ],
                    exports: [
                        UnespCoreAccessDeniedModule,
                        UnespCoreFooterModule,
                        UnespCoreHeaderModule,
                        UnespCoreHeaderCountdownModule,
                        UnespCoreHomeModule,
                        UnespCorePageNotFoundModule,
                        UnespCoreSidenavModule,
                        UnespCoreSidenavItemModule,
                        UnespCoreLayoutModule
                    ],
                    providers: [],
                }]
        }], ctorParameters: () => [{ type: i3$2.MatIconRegistry }, { type: i2$3.DomSanitizer }] });

/**
 * @description
 *
 * Este *interceptor* tem a finalidade de injetar corretamente os tokens (JWT/XSRF) e tratar possíveis erros retornados pela API para estes tokens.
 *
 * Pode ser utilizado para dar feedback das ações do usuário como, por exemplo: erro de autorização, mensagens de regras de negócio
 * e erros de indisponibilidade do backend.
 *
 * Ao importar o módulo `UnespCoreModule` na aplicação, o *interceptor* é automaticamente configurado sem a necessidade
 * de qualquer configuração extra.
 *
 * Ao realizar requisições utilize o `HttpClient`, conforme exemplo abaixo:
 *
 *
 * ```
 * ...
 *
 * import { HttpClient } from '@angular/common/http';
 *
 *
 * @Injectable()
 * export class UserService {
 *
 *   constructor(private http: HttpClient) { }
 *
 *   getUsers() {
 *     return this.http.get('/api/users');
 *   }
 *
 *   ...
 *
 * }
 * ```
 *
 */
class UnespCoreAuthInterceptorService {
    constructor(unespCoreAuthService, tokenExtractor) {
        this.unespCoreAuthService = unespCoreAuthService;
        this.tokenExtractor = tokenExtractor;
        this.refreshingInProgress = false;
        this.accessTokenSubject = new BehaviorSubject(false);
        this.headerXsrf = 'X-XSRF-TOKEN';
    }
    intercept(request, next) {
        const token = this.tokenExtractor.getToken();
        if (token !== null && !request.headers.has(this.headerXsrf)) {
            request = request.clone({
                headers: request.headers.set(this.headerXsrf, token),
                withCredentials: true
            });
        }
        else {
            request = request.clone({
                withCredentials: true
            });
        }
        return next.handle(request).pipe(catchError(err => {
            // No caso de erro 401 Unauthorized
            if (err instanceof HttpErrorResponse && err.status === 401 && !request.url.includes('auth/logout')) {
                return this.refreshToken(request, next);
            }
            // No caso de erro 403 Forbidden (refresh token falhou)
            if (err instanceof HttpErrorResponse && err.status === 403) {
                if (request.url.includes('auth/refresh-token')) {
                    // logout and redirect to login page
                    this.unespCoreAuthService.logout('Sessão expirada', true);
                }
                else {
                    // logout and redirect to login page
                    if (err.error && err.error[0]?.mensagemUsuario) {
                        this.unespCoreAuthService.logout(err.error[0].mensagemUsuario, true);
                    }
                    else {
                        this.unespCoreAuthService.logout('Sessão expirada', true);
                    }
                }
            }
            // Retornar outros tipos de erros
            return throwError(err);
        }));
    }
    refreshToken(request, next) {
        if (!this.refreshingInProgress) {
            this.refreshingInProgress = true;
            this.accessTokenSubject.next(false);
            return this.unespCoreAuthService.refreshToken().pipe(switchMap((res) => {
                this.refreshingInProgress = false;
                this.accessTokenSubject.next(true);
                this.unespCoreAuthService.renewAquiredTokenTimeleft();
                // Repete a requisição com falha mas agora com novo token
                return next.handle(request);
            }));
        }
        else {
            // Espera enquanto obtem um novo token - Fila
            return this.accessTokenSubject.pipe(filter(token => token), take(1), switchMap(token => {
                // Repete a requisição com falha mas agora com novo token
                this.unespCoreAuthService.renewAquiredTokenTimeleft();
                return next.handle(request);
            }));
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthInterceptorService, deps: [{ token: UnespCoreAuthService }, { token: i1$1.HttpXsrfTokenExtractor }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthInterceptorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthInterceptorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UnespCoreAuthService }, { type: i1$1.HttpXsrfTokenExtractor }] });

class UnespCoreAuthInterceptorModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthInterceptorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthInterceptorModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthInterceptorModule, providers: [
            {
                provide: HTTP_INTERCEPTORS, useClass: UnespCoreAuthInterceptorService, multi: true
            }
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreAuthInterceptorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    providers: [
                        {
                            provide: HTTP_INTERCEPTORS, useClass: UnespCoreAuthInterceptorService, multi: true
                        }
                    ]
                }]
        }] });

/**
 * @description
 *
 * Este *interceptor* tem a finalidade de inserir um loader enquanto a resposta de uma API está sendo aguardada.
 *
 * Ao importar o módulo `UnespCoreModule` na aplicação, o *interceptor* é automaticamente configurado sem a necessidade
 * de qualquer configuração extra.
 *
 */
class UnespCoreLoaderRequestInterceptorService {
    constructor(unespCoreLoaderService) {
        this.unespCoreLoaderService = unespCoreLoaderService;
        // Para dar suporte a várias chamadas
        this.serviceCount = 0;
    }
    intercept(req, next) {
        this.unespCoreLoaderService.isLoading.next(true);
        this.serviceCount++;
        return next.handle(req).pipe(finalize(() => {
            this.serviceCount--;
            if (this.serviceCount === 0) {
                this.unespCoreLoaderService.isLoading.next(false);
            }
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderRequestInterceptorService, deps: [{ token: UnespCoreLoaderService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderRequestInterceptorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderRequestInterceptorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UnespCoreLoaderService }] });

class UnespCoreLoaderRequestInterceptorModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderRequestInterceptorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderRequestInterceptorModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderRequestInterceptorModule, providers: [
            {
                provide: HTTP_INTERCEPTORS, useClass: UnespCoreLoaderRequestInterceptorService, multi: true,
            }
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreLoaderRequestInterceptorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    providers: [
                        {
                            provide: HTTP_INTERCEPTORS, useClass: UnespCoreLoaderRequestInterceptorService, multi: true,
                        }
                    ]
                }]
        }] });

class UnespCoreInterceptorsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreInterceptorsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreInterceptorsModule, imports: [UnespCoreAuthInterceptorModule,
            UnespCoreLoaderRequestInterceptorModule], exports: [UnespCoreAuthInterceptorModule,
            UnespCoreLoaderRequestInterceptorModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreInterceptorsModule, imports: [UnespCoreAuthInterceptorModule,
            UnespCoreLoaderRequestInterceptorModule, UnespCoreAuthInterceptorModule,
            UnespCoreLoaderRequestInterceptorModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreInterceptorsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        UnespCoreAuthInterceptorModule,
                        UnespCoreLoaderRequestInterceptorModule
                    ],
                    exports: [
                        UnespCoreAuthInterceptorModule,
                        UnespCoreLoaderRequestInterceptorModule
                    ]
                }]
        }] });

class UnespCoreModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreModule, imports: [UnespCoreComponentsModule,
            UnespCoreInterceptorsModule,
            UnespCoreServicesModule,
            BrowserModule,
            BrowserAnimationsModule], exports: [UnespCoreComponentsModule,
            UnespCoreInterceptorsModule,
            UnespCoreServicesModule,
            BrowserModule,
            BrowserAnimationsModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreModule, providers: [provideHttpClient(withInterceptorsFromDi())], imports: [UnespCoreComponentsModule,
            UnespCoreInterceptorsModule,
            UnespCoreServicesModule,
            BrowserModule,
            BrowserAnimationsModule, UnespCoreComponentsModule,
            UnespCoreInterceptorsModule,
            UnespCoreServicesModule,
            BrowserModule,
            BrowserAnimationsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.1.7", ngImport: i0, type: UnespCoreModule, decorators: [{
            type: NgModule,
            args: [{ declarations: [],
                    exports: [
                        UnespCoreComponentsModule,
                        UnespCoreInterceptorsModule,
                        UnespCoreServicesModule,
                        BrowserModule,
                        BrowserAnimationsModule,
                    ], imports: [UnespCoreComponentsModule,
                        UnespCoreInterceptorsModule,
                        UnespCoreServicesModule,
                        BrowserModule,
                        BrowserAnimationsModule], providers: [provideHttpClient(withInterceptorsFromDi())] }]
        }] });

/**
 * Animação referente a navegação entre rotas
 */
const resetRoute = [
    style({ position: 'relative' }),
    query(':enter, :leave', [
        style({
            position: 'absolute',
            width: '100%',
            opacity: 0,
        }),
    ], { optional: true }),
];
/**
 * Animação referente a navegação entre rotas
 */
const routeAnimations = trigger('routeAnimations', [
    transition('* => *', [
        ...resetRoute,
        query(':enter', [style({ opacity: 0 })], {
            optional: true,
        }),
        group([
            query(':leave', [style({ opacity: 1 }), animate('0.250s', style({ opacity: 0 }))], { optional: true }),
            query(':enter', [style({ opacity: 0 }), animate('0.250s', style({ opacity: 1 }))], { optional: true }),
        ]),
    ]),
]);
/**
 * Animação ao exibir lista de resultados em MatTable
 */
const rowsMatTableAnimation = trigger('rowsMatTableAnimation', [
    transition('void => *', [
        style({ height: '*', opacity: '0', transform: 'translateX(-550px)', 'box-shadow': 'none' }),
        sequence([
            animate('.35s ease', style({ height: '*', opacity: '.2', transform: 'translateX(0)', 'box-shadow': 'none' })),
            animate('.35s ease', style({ height: '*', opacity: 1, transform: 'translateX(0)' }))
        ])
    ])
]);

/*
 * Public API Surface of unesp-core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ROLE_WILDCARD_ONLYUSERS, ROLE_WILDCARD_PUBLIC, UnespCoreAccessDeniedComponent, UnespCoreAccessDeniedModule, UnespCoreApiErrorHandlerModule, UnespCoreApiErrorHandlerService, UnespCoreAuthGuardModule, UnespCoreAuthGuardService, UnespCoreAuthInterceptorModule, UnespCoreAuthInterceptorService, UnespCoreAuthModule, UnespCoreAuthService, UnespCoreComponentsModule, UnespCoreFooterComponent, UnespCoreFooterModule, UnespCoreGenericService, UnespCoreGenericServiceModule, UnespCoreHeaderComponent, UnespCoreHeaderCountdownComponent, UnespCoreHeaderCountdownModule, UnespCoreHeaderModule, UnespCoreHomeComponent, UnespCoreHomeModule, UnespCoreInterceptorsModule, UnespCoreLayoutComponent, UnespCoreLayoutModule, UnespCoreLoaderModule, UnespCoreLoaderRequestInterceptorModule, UnespCoreLoaderRequestInterceptorService, UnespCoreLoaderService, UnespCoreMenuItem, UnespCoreMenuNavigationModule, UnespCoreMenuNavigationService, UnespCoreMessageModule, UnespCoreMessageService, UnespCoreMessageType, UnespCoreModule, UnespCorePageNotFoundComponent, UnespCorePageNotFoundModule, UnespCoreServicesModule, UnespCoreSidenavComponent, UnespCoreSidenavItemComponent, UnespCoreSidenavItemModule, UnespCoreSidenavModule, UnespCoreUserModule, UnespCoreUserService, routeAnimations, rowsMatTableAnimation };
